'use client';

import { c as _c } from "react/compiler-runtime";
import { parseJson } from '@gnsx/genesys.js';
import { useLocalStorageValue } from '@react-hookz/web';
import { ChevronDown, ChevronRight, ChevronsUpDownIcon, Download, FolderIcon, Loader2, Map as MapIcon, MoreHorizontal, RefreshCw, RotateCcwIcon, Sparkles, ZoomIn } from 'lucide-react';
import { Check, WandSparklesIcon } from 'lucide-react';
import { Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FlipbookCube } from './components/FlipbookCube';
import { GenerateButton } from './components/GenerateButton';
import { GenericModelSelector } from './components/GenericModelSelector';
import { OptimizedScenarioImage } from './components/OptimizedScenarioImage';
import { getMagicPromptDescription, getModeExplanation, isMagicPromptAvailable, useMagicPrompt } from './MagicPrompts';
import { ReferenceImageSelector } from './ReferenceImageSelector';
import type { GenerationType, ReferenceImage, ScenarioAsset, ScenarioAssetGeneration, ScenarioImageModel, ScenarioImageParams, ScenarioModel, ScenarioModel3D, ScenarioModel3DParams, ScenarioSkyboxParams, ScenarioSkyboxStyleString, ScenarioTextureParams } from '@/lib/scenario';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { InputNumber } from '@/components/ui/input-number';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AutosizeTextarea } from '@/components/ui/textarea-autosize';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TEST_USER_OVERRIDE, TEST_USERS } from '@/const';
import { useAuth } from '@/context/AuthContext';
import { editorAssets, editorStore } from '@/editor/store';
import { AssetFilters, DefaultModelPreferences, modelCapabilities, ModelCapabilityFilters, SkyboxStyleCategories } from '@/lib/scenario';
import { scenarioClient, scenarioService } from '@/lib/scenario';
import { ScenarioAPIError } from '@/lib/scenario/scenarioErrors';
import { cn } from '@/lib/utils';
const SHOW_MagicPromptTestButton = false && process.env.NODE_ENV === 'development';

/**
 * Debug logging controls for ScenarioGenerationDialog
 *
 * Enable specific flags to debug tab switching performance issues:
 *
 * - tabSwitching: Cache hit/miss operations during tab switches
 * - dataLoading: API calls and data fetching operations
 * - cacheOperations: Cache invalidation after generation/upscale/conversion
 * - raceConditions: Request ID tracking for race condition debugging
 */
const DEBUG = {
  tabSwitching: false,
  // Tab switch cache operations
  dataLoading: false,
  // Historical data loading
  cacheOperations: false,
  // Cache invalidation and updates
  raceConditions: false // Request ID tracking
} as const;

/** Storage key suffix for scenario generation preferences */
const SCENARIO_PREFS_KEY = 'scenarioGenerationPrefs';

/** Persisted scenario generation preferences (per-project) */
interface ScenarioGenerationPrefs {
  selectedTextureModelId?: string;
  selectedImageModelId?: string;
  selectedModel3DId?: string;
  selectedSkyboxStyle?: ScenarioSkyboxStyleString;
}
interface ScenarioGenerationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}
interface GenerationThumbnail {
  id: string;
  status: 'queued' | 'generating' | 'completed' | 'failed' | 'error';
  progress: number;
  imageUrl?: string | null;
  assetId?: string | null;
}
interface ModelSelectorProps {
  selectedModel: ScenarioImageModel | null;
  onModelSelect: (model: ScenarioImageModel) => void;
  disabled?: boolean;
}
interface SkyboxStyleSelectorProps {
  selectedStyle: ScenarioSkyboxStyleString | null;
  onStyleSelect: (style: ScenarioSkyboxStyleString) => void;
  disabled?: boolean;
}
interface ImageModelSelectorProps {
  selectedModel: ScenarioImageModel | null;
  onModelSelect: (model: ScenarioImageModel) => void;
  disabled?: boolean;
}
interface Model3DSelectorProps {
  selectedModel: ScenarioModel3D | null;
  onModelSelect: (model: ScenarioModel3D) => void;
  disabled?: boolean;
}

// Import FlipbookCube from components

/** Helper function to load texture models - delegates to scenarioClient */
const loadTextureModels = (options?: {
  forceRefresh?: boolean;
}) => scenarioClient.getTextureModels(options);

/** Helper function to load image models - delegates to scenarioClient */
const loadImageModels = (options?: {
  forceRefresh?: boolean;
}) => scenarioClient.getImageModels(options);

/** Helper function to load 3D models - delegates to scenarioClient */
const loadModel3DList = (options?: {
  forceRefresh?: boolean;
}) => scenarioClient.get3DModels(options);

/** Texture Model Selector */
const TextureModelSelector = t0 => {
  const $ = _c(5);
  const {
    selectedModel,
    onModelSelect,
    disabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...ModelCapabilityFilters.texture];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== disabled || $[2] !== onModelSelect || $[3] !== selectedModel) {
    t2 = <GenericModelSelector selectedModel={selectedModel} onModelSelect={onModelSelect} disabled={disabled} label="Texture Model" dialogTitle="Choose a Texture Model" dialogDescription="Select a texture generation model for your project." loadModels={loadTextureModels} capabilityFilters={t1} />;
    $[1] = disabled;
    $[2] = onModelSelect;
    $[3] = selectedModel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/**
 * Skybox style selector matching Scenario's UI with side-by-side layout
 */
const SkyboxStyleSelector = t0 => {
  const $ = _c(39);
  const {
    selectedStyle,
    onStyleSelect,
    disabled
  } = t0;
  const [hoveredStyle, setHoveredStyle] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] !== selectedStyle) {
    const getSelectedStyleInfo = () => {
      for (const category of SkyboxStyleCategories) {
        const found = category.styles.find(s => s.value === selectedStyle);
        if (found) {
          return found;
        }
      }
      return null;
    };
    t1 = getSelectedStyleInfo();
    $[0] = selectedStyle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedStyleInfo = t1;
  let t2;
  if ($[2] !== onStyleSelect) {
    t2 = style => {
      onStyleSelect(style.value);
      setIsOpen(false);
    };
    $[2] = onStyleSelect;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleStyleSelect = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Label className="text-sm font-medium">Style</Label>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== selectedStyleInfo) {
    t4 = selectedStyleInfo ? <div className="flex items-center gap-2"><OptimizedScenarioImage src={selectedStyleInfo.image} alt={selectedStyleInfo.label} aspectRatio="auto" className="w-6 h-6 rounded" /><span>{selectedStyleInfo.label}</span></div> : <span>Select a style...</span>;
    $[5] = selectedStyleInfo;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== disabled || $[9] !== isOpen || $[10] !== t4) {
    t6 = <PopoverTrigger asChild={true}><Button size="lg" role="combobox" aria-expanded={isOpen} className="w-full justify-between" disabled={disabled}>{t4}{t5}</Button></PopoverTrigger>;
    $[8] = disabled;
    $[9] = isOpen;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== handleStyleSelect || $[13] !== selectedStyle) {
    t7 = SkyboxStyleCategories.map(category_0 => <div key={category_0.category}><div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-background/80 sticky top-0 border-b border-border">{category_0.category}</div>{category_0.styles.map(style_0 => <div key={style_0.value} className={`px-3 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors ${selectedStyle === style_0.value ? "bg-accent text-accent-foreground" : ""}`} onMouseEnter={() => setHoveredStyle({
        value: style_0.value,
        image: style_0.image,
        label: style_0.label,
        description: style_0.description
      })} onMouseLeave={() => setHoveredStyle(null)} onClick={() => handleStyleSelect(style_0)}><div className="flex items-center gap-3"><OptimizedScenarioImage src={style_0.image} alt={style_0.label} aspectRatio="auto" className="w-12 h-12 rounded shrink-0" /><div className="flex-1 min-w-0"><div className="font-medium text-sm">{style_0.label}</div><div className="text-xs text-muted-foreground truncate">{style_0.description}</div></div>{selectedStyle === style_0.value && <Check className="h-4 w-4 text-primary" />}</div></div>)}</div>);
    $[12] = handleStyleSelect;
    $[13] = selectedStyle;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <div className="w-80 border-r border-border overflow-hidden"><div className="h-96 overflow-y-auto" tabIndex={0} onWheel={_temp}><div className="p-1">{t7}</div></div></div>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== hoveredStyle || $[18] !== selectedStyleInfo) {
    t9 = (hoveredStyle || selectedStyleInfo) && <OptimizedScenarioImage src={(hoveredStyle || selectedStyleInfo).image} alt="Style preview" aspectRatio="auto" className="max-w-full max-h-full rounded-lg shadow-lg object-contain" />;
    $[17] = hoveredStyle;
    $[18] = selectedStyleInfo;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== hoveredStyle || $[21] !== selectedStyleInfo) {
    t10 = !hoveredStyle && !selectedStyleInfo && <div className="flex items-center justify-center text-sm text-muted-foreground h-48 w-full border-2 border-dashed border-muted-foreground/20 rounded-lg">Hover a style to preview</div>;
    $[20] = hoveredStyle;
    $[21] = selectedStyleInfo;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className="flex-1 flex items-center justify-center min-h-0">{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== hoveredStyle || $[27] !== selectedStyleInfo) {
    t12 = (hoveredStyle || selectedStyleInfo) && <div className="mt-3 space-y-1"><div className="font-medium text-sm">{(hoveredStyle || selectedStyleInfo).label}</div><div className="text-xs text-muted-foreground">{(hoveredStyle || selectedStyleInfo).description}</div></div>;
    $[26] = hoveredStyle;
    $[27] = selectedStyleInfo;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <div className="flex-1 p-6 flex flex-col">{t11}{t12}</div>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t8) {
    t14 = <PopoverContent className="w-[900px] p-0" align="start"><div className="flex h-96">{t8}{t13}</div></PopoverContent>;
    $[32] = t13;
    $[33] = t8;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== isOpen || $[36] !== t14 || $[37] !== t6) {
    t15 = <div className="space-y-2">{t3}<Popover open={isOpen} onOpenChange={setIsOpen}>{t6}{t14}</Popover></div>;
    $[35] = isOpen;
    $[36] = t14;
    $[37] = t6;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
};

/** Image Model Selector */
const ImageModelSelector = t0 => {
  const $ = _c(5);
  const {
    selectedModel,
    onModelSelect,
    disabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...ModelCapabilityFilters.image];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== disabled || $[2] !== onModelSelect || $[3] !== selectedModel) {
    t2 = <GenericModelSelector selectedModel={selectedModel} onModelSelect={onModelSelect} disabled={disabled} label="Image Model" dialogTitle="Choose an Image Model" dialogDescription="Select an image generation model for your project." loadModels={loadImageModels} showFeaturedBadge={true} capabilityFilters={t1} />;
    $[1] = disabled;
    $[2] = onModelSelect;
    $[3] = selectedModel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const Model3DSelector = t0 => {
  const $ = _c(5);
  const {
    selectedModel,
    onModelSelect,
    disabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...ModelCapabilityFilters.model3D];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== disabled || $[2] !== onModelSelect || $[3] !== selectedModel) {
    t2 = <GenericModelSelector selectedModel={selectedModel} onModelSelect={onModelSelect} disabled={disabled} label="3D Model" dialogTitle="Choose a 3D Model" dialogDescription="Select a 3D generation model for your project." loadModels={loadModel3DList} placeholder="Select a 3D model" capabilityFilters={t1} />;
    $[1] = disabled;
    $[2] = onModelSelect;
    $[3] = selectedModel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/**
 * Component to display a single 3D generation with video preview that plays on hover
 */
const ThreeDGenerationItem = t0 => {
  const $ = _c(17);
  const {
    generation,
    onSaveToProject,
    isSaving
  } = t0;
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [isLoadingPreview] = useState(false);
  const [glbAsset, setGlbAsset] = useState(null);
  let t1;
  if ($[0] !== generation.assets) {
    t1 = () => {
      const findAssets = async () => {
        const foundGlbAsset = generation.assets.find(_temp2);
        if (foundGlbAsset) {
          setGlbAsset(foundGlbAsset);
          const assetWithPreview = foundGlbAsset as ScenarioAsset & {
            preview?: {
              url: string;
            };
          };
          if (assetWithPreview.preview?.url) {
            setPreviewVideoUrl(assetWithPreview.preview.url);
            return;
          }
        }
        const mp4Asset = generation.assets.find(_temp3);
        if (mp4Asset) {
          setPreviewVideoUrl(mp4Asset.url);
          return;
        }
      };
      findAssets();
    };
    $[0] = generation.assets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== generation.assets || $[3] !== generation.jobId) {
    t2 = [generation.jobId, generation.assets];
    $[2] = generation.assets;
    $[3] = generation.jobId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== generation.assets || $[6] !== isLoadingPreview || $[7] !== previewVideoUrl) {
    t3 = isLoadingPreview ? <div className="w-full h-full flex items-center justify-center bg-gray-700 text-white"><Loader2 className="h-6 w-6 animate-spin" /></div> : previewVideoUrl ? <video src={previewVideoUrl} className="w-full h-full object-cover" muted={true} loop={true} playsInline={true} onMouseEnter={_temp5} onMouseLeave={_temp6} /> : <div className="w-full h-full flex flex-col items-center justify-center bg-gray-700 text-white p-2"><div className="text-xs text-center">No Preview Video</div><div className="text-xs text-center text-gray-500 mt-1">Assets: {generation.assets.map(_temp7).join(", ")}</div></div>;
    $[5] = generation.assets;
    $[6] = isLoadingPreview;
    $[7] = previewVideoUrl;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">3D Preview</div>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== glbAsset || $[11] !== isSaving || $[12] !== onSaveToProject) {
    t5 = glbAsset && <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"><DropdownMenu><DropdownMenuTrigger asChild={true}><Button variant="secondary" size="sm" className="h-6 w-6 p-0 bg-black/50 hover:bg-black/70 border-none"><MoreHorizontal className="h-3 w-3 text-white" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-36 text-xs"><DropdownMenuItem onClick={() => onSaveToProject(glbAsset.id, `historical-${glbAsset.id}`)} disabled={isSaving} className="text-xs">{isSaving ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Saving...</> : <><Download className="mr-1 h-3 w-3" />Save 3D Model</>}</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[10] = glbAsset;
    $[11] = isSaving;
    $[12] = onSaveToProject;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = <div className="bg-background rounded-lg overflow-hidden border hover:border-primary transition-colors cursor-pointer relative group aspect-square">{t3}{t4}{t5}</div>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};

// Helper functions (moved outside component for better performance)

/** Detect 3D model type from capabilities */
const getModel3DType = (model: ScenarioModel3D | null): 'img23d' | 'txt23d' | null => {
  if (!model?.capabilities) return null;
  if (model.capabilities.includes('txt23d')) return 'txt23d';
  if (model.capabilities.includes('img23d')) return 'img23d';
  return null;
};
const shouldDisplayPrompt = (prompt: string): boolean => {
  if (!prompt || !prompt.trim()) return false;
  const genericPrompts = ['generated content', 'test', 'placeholder', 'untitled', 'default', 'sample'];
  const lowerPrompt = prompt.toLowerCase().trim();
  return !genericPrompts.includes(lowerPrompt);
};
const isOriginalTexture = (asset: ScenarioAssetGeneration): boolean => {
  const result = asset.assets.some(a => {
    const type = a.metadata?.type;
    if (type === 'Text to Image' || type === 'text-to-image' || type === 'texture' || type === 'inference-txt2img-texture') return true;
    if (type && (type.includes('texture-albedo') || type.includes('texture-normal') || type.includes('texture-height') || type.includes('texture-metallic') || type.includes('texture-smoothness') || type.includes('texture-roughness') || type.includes('texture-ao') || type.includes('texture-edge') || type.includes('albedo') || type.includes('normal') || type.includes('height') || type.includes('metallic') || type.includes('smoothness') || type.includes('roughness') || type.includes('ao') || type.includes('edge') || type.includes('Map') || type.includes('map'))) {
      return false;
    }
    return true;
  });
  return result;
};
const isTextureMap = (asset: ScenarioAsset): boolean => {
  const type = asset.metadata?.type || '';
  return type.includes('texture-albedo') || type.includes('texture-normal') || type.includes('texture-height') || type.includes('texture-metallic') || type.includes('texture-smoothness') || type.includes('texture-roughness') || type.includes('texture-ao') || type.includes('texture-edge') || type.includes('Map') || type.includes('map') || type.includes('albedo') && type !== 'texture' || type.includes('normal') && type !== 'texture' || type.includes('height') && type !== 'texture' || type.includes('metallic') && type !== 'texture' || type.includes('smoothness') && type !== 'texture' || type.includes('roughness') && type !== 'texture' || type.includes('ao') && type !== 'texture' || type.includes('edge') && type !== 'texture';
};
const getMapType = (asset: ScenarioAsset): string => {
  const type = asset.metadata?.type || '';
  if (type.includes('texture-albedo') || type.includes('albedo') || type.includes('Albedo')) return 'Albedo';
  if (type.includes('texture-normal') || type.includes('normal') || type.includes('Normal')) return 'Normal';
  if (type.includes('texture-height') || type.includes('height') || type.includes('Height')) return 'Height';
  if (type.includes('texture-metallic') || type.includes('metallic') || type.includes('Metallic')) return 'Metallic';
  if (type.includes('texture-smoothness') || type.includes('smoothness') || type.includes('Smoothness')) return 'Smoothness';
  if (type.includes('texture-roughness') || type.includes('roughness') || type.includes('Roughness')) return 'Roughness';
  if (type.includes('texture-ao') || type.includes('ao') || type.includes('AO')) return 'AO';
  if (type.includes('texture-edge') || type.includes('edge') || type.includes('Edge')) return 'Edge';
  if (type === 'texture') return 'Material Preview';
  return type;
};
type GroupedTextureItem = {
  type: 'original' | 'set';
  generation: ScenarioAssetGeneration;
  maps?: ScenarioAssetGeneration[];
  flipbookAsset?: ScenarioAsset | null;
  expanded?: boolean;
};

/** Load scenario generation preferences from localStorage */
const loadScenarioPrefs = (): ScenarioGenerationPrefs => {
  try {
    const key = editorStore.makePerProjectSaveKey(SCENARIO_PREFS_KEY);
    const stored = localStorage.getItem(key);
    return stored ? parseJson(stored) : {};
  } catch {
    return {};
  }
};

/** Save scenario generation preferences to localStorage */
const saveScenarioPrefs = (prefs: Partial<ScenarioGenerationPrefs>) => {
  try {
    const key = editorStore.makePerProjectSaveKey(SCENARIO_PREFS_KEY);
    const existing = loadScenarioPrefs();
    localStorage.setItem(key, JSON.stringify({
      ...existing,
      ...prefs
    }));
  } catch {
    // Silently fail if localStorage unavailable
  }
};

// Params that are already handled by dedicated UI controls (don't render dynamically)
const HANDLED_IMAGE_PARAMS = new Set(['prompt', 'referenceImages', 'images', 'imagePrompt', 'image', 'imageReferenceUrl', 'subjectReference', 'characterReferenceImage', 'controlImage',
// Reference image params
'seed', 'numSamples', 'numOutputs',
// Count/seed params
'numInferenceSteps', 'steps',
// Steps params
'guidance', 'guidanceScale',
// Guidance params
'width', 'height', 'aspectRatio',
// Dimension params
'influence', 'promptStrength', 'imageReferenceWeight', 'strength' // Influence params
]);

/**
 * Dialog for AI generation using Scenario (Skyboxes, Textures, and Images)
 */
export const ScenarioGenerationDialog = observer(({
  isOpen,
  onOpenChange
}: ScenarioGenerationDialogProps) => {
  const {
    firebaseUser
  } = useAuth();

  // Add migration utility to global scope for one-time use
  useEffect(() => {
    // Migration utility - disabled in production
    // if (typeof window !== 'undefined') {
    //   interface WindowWithMigration extends Window {
    //     tagAllExistingAssets?: (dryRun?: boolean) => Promise<void>;
    //   }
    //   (window as WindowWithMigration).tagAllExistingAssets = async (dryRun: boolean = true) => {
    //     try {
    //       await scenarioService.tagAllExistingAssets(firebaseUser?.sub, dryRun);
    //     } catch (error) {
    //       console.error('Migration failed:', error);
    //     }
    //   };
    // }
  }, [firebaseUser?.sub]);

  // Shared state
  const [activeTab, setActiveTab] = useState<'skybox' | 'texture' | 'image' | '3d'>('3d');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<GenerationThumbnail[]>([]);
  // Magic Prompt functionality
  const {
    enhancePrompt: enhanceMagicPrompt,
    isEnhancing: isEnhancingPrompt,
    error: magicPromptError,
    clearError: clearMagicPromptError
  } = useMagicPrompt();
  const [isSaving, setIsSaving] = useState(false);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [historicalGenerations, setHistoricalGenerations] = useState<ScenarioAssetGeneration[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isPollingForAssets, setIsPollingForAssets] = useState(false);

  /** Per-tab historical data cache for instant tab switching */
  const [historicalGenerationsCache, setHistoricalGenerationsCache] = useState<{
    skybox: ScenarioAssetGeneration[];
    texture: ScenarioAssetGeneration[];
    image: ScenarioAssetGeneration[];
    '3d': ScenarioAssetGeneration[];
  }>({
    skybox: [],
    texture: [],
    image: [],
    '3d': []
  });

  /** Track active load request to prevent race conditions on rapid tab switching */
  const activeLoadRequestRef = useRef<string | null>(null);

  /** Debounce rapid tab switches */
  const tabSwitchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /** Track active polling to allow cancellation */
  const pollingStoppedRef = useRef<boolean>(false);

  /** Track when each tab was last loaded (timestamp) for cache invalidation */
  const tabLoadTimestampRef = useRef<Map<string, number>>(new Map());
  const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

  // Save folder paths for each tab with local storage persistence
  const skyboxSaveFolderStorage = useLocalStorageValue('scenarioGen_skyboxSaveFolder', {
    defaultValue: 'assets/generated/skybox'
  });
  const textureSaveFolderStorage = useLocalStorageValue('scenarioGen_textureSaveFolder', {
    defaultValue: 'assets/generated/textures'
  });
  const imageSaveFolderStorage = useLocalStorageValue('scenarioGen_imageSaveFolder', {
    defaultValue: 'assets/generated/images'
  });
  const threeDSaveFolderStorage = useLocalStorageValue('scenarioGen_threeDSaveFolder', {
    defaultValue: 'assets/generated/mesh'
  });
  const skyboxSaveFolder = skyboxSaveFolderStorage.value ?? 'assets/generated/skybox';
  const textureSaveFolder = textureSaveFolderStorage.value ?? 'assets/generated/textures';
  const imageSaveFolder = imageSaveFolderStorage.value ?? 'assets/generated/images';
  const threeDSaveFolder = threeDSaveFolderStorage.value ?? 'assets/generated/mesh';

  // Path picker states
  const [skyboxPathPickerOpen, setSkyboxPathPickerOpen] = useState(false);
  const [texturePathPickerOpen, setTexturePathPickerOpen] = useState(false);
  const [imagePathPickerOpen, setImagePathPickerOpen] = useState(false);
  const [threeDPathPickerOpen, setThreeDPathPickerOpen] = useState(false);

  // Skybox-specific state
  const [skyboxPrompt, setSkyboxPrompt] = useState('');
  const [skyboxReferenceImage, setSkyboxReferenceImage] = useState<ReferenceImage | null>(null);
  const [skyboxCount, setSkyboxCount] = useState(1);
  const [skyboxSeed, setSkyboxSeed] = useState<number | undefined>();
  const [skyboxSamplingSteps, setSkyboxSamplingSteps] = useState(30);
  const [geometryEnforcement, setGeometryEnforcement] = useState(0);
  const [selectedSkyboxStyle, setSelectedSkyboxStyle] = useState<ScenarioSkyboxStyleString | null>(null);

  // Texture-specific state
  const [texturePrompt, setTexturePrompt] = useState('');
  const [textureReferenceImage, setTextureReferenceImage] = useState<ReferenceImage | null>(null);
  const [selectedModel, setSelectedModel] = useState<ScenarioImageModel | null>(null);
  const [textureCount, setTextureCount] = useState(1);
  const [textureSeed, setTextureSeed] = useState<number | undefined>();
  const [textureSamplingSteps, setTextureSamplingSteps] = useState(30);
  const [guidance, setGuidance] = useState(7.5);
  const [textureWidth, setTextureWidth] = useState(512);
  const [textureHeight, setTextureHeight] = useState(512);
  const [tiling, setTiling] = useState(true);
  const [seamless, setSeamless] = useState(true);

  // Image-specific state
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageReferenceImage, setImageReferenceImage] = useState<ReferenceImage | null>(null);
  const [selectedImageModel, setSelectedImageModel] = useState<ScenarioImageModel | null>(null);
  const [isLoadingImageModelInputs, setIsLoadingImageModelInputs] = useState(false);
  const [imageCount, setImageCount] = useState(1);
  const [imageSeed, setImageSeed] = useState<number | undefined>();
  const [negativePrompt, setNegativePrompt] = useState('');
  const [imageSamplingSteps, setImageSamplingSteps] = useState(30);
  const [imageGuidance, setImageGuidance] = useState(7.5);
  const [imageWidth, setImageWidth] = useState(2048);
  const [imageHeight, setImageHeight] = useState(2048);
  // Dynamic image parameters based on selected model's inputs
  const [imageModelParams, setImageModelParams] = useState<Record<string, string | number | boolean>>({});
  // Aspect ratio for models that use aspectRatio instead of width/height
  const [imageAspectRatio, setImageAspectRatio] = useState<string>('1:1');

  // 3D-specific state
  const [selectedModel3D, setSelectedModel3D] = useState<ScenarioModel3D | null>(null);
  // Dynamic 3D parameters based on selected model's inputs
  const [model3DParams, setModel3DParams] = useState<Record<string, string | number | boolean>>({});
  // Multi-view image state for 3D models
  const [model3DImages, setModel3DImages] = useState<Record<string, ReferenceImage | null>>({});

  // Initialize 3D parameters when model changes
  const initializeModel3DParams = (model: ScenarioModel3D | null) => {
    if (!model?.inputs) {
      setModel3DParams({});
      setModel3DImages({});
      return;
    }
    const newParams: Record<string, string | number | boolean> = {};
    const newImages: Record<string, ReferenceImage | null> = {};
    model.inputs.forEach(input => {
      if (input.type === 'file') {
        newImages[input.name] = null;
      } else {
        if (input.default !== undefined) {
          newParams[input.name] = input.default;
        } else {
          newParams[input.name] = input.type === 'number' ? 0 : input.type === 'boolean' ? false : '';
        }
      }
    });
    setModel3DParams(newParams);
    setModel3DImages(newImages);
  };

  // Initialize image model parameters when model changes
  const initializeImageModelParams = useCallback((model: ScenarioImageModel | null) => {
    if (!model?.inputs) {
      setImageModelParams({});
      return;
    }
    const newParams: Record<string, string | number | boolean> = {};
    model.inputs.forEach(input => {
      // Skip params that have dedicated UI controls
      if (HANDLED_IMAGE_PARAMS.has(input.name)) return;
      // Skip file/assetId types as they need special handling
      if (input.type === 'file' || input.type === 'assetId' || input.type === 'assetId[]') return;
      if (input.default !== undefined && input.default !== null) {
        // Coerce default to expected type
        const defaultVal = input.default;
        if (typeof defaultVal === 'string' || typeof defaultVal === 'number' || typeof defaultVal === 'boolean') {
          newParams[input.name] = defaultVal;
        } else {
          newParams[input.name] = String(defaultVal);
        }
      } else if (input.type === 'number') {
        newParams[input.name] = input.min ?? 0;
      } else if (input.type === 'boolean') {
        newParams[input.name] = false;
      } else if (input.type === 'string' && input.allowedValues?.length) {
        newParams[input.name] = input.allowedValues[0];
      } else {
        newParams[input.name] = '';
      }
    });
    setImageModelParams(newParams);
  }, []);

  // Dynamic model capabilities hook
  const currentModelCapabilities = useMemo(() => {
    if (!selectedImageModel) return null;
    return modelCapabilities.getImageModelCapabilities(selectedImageModel);
  }, [selectedImageModel]);

  // Dynamic dimensions based on model capabilities
  const availableDimensions = useMemo(() => {
    if (!currentModelCapabilities) {
      // Default dimensions if no model selected
      return [{
        width: 1344,
        height: 768,
        label: '1344x768 (16:9)'
      }, {
        width: 1216,
        height: 832,
        label: '1216x832 (3:2)'
      }, {
        width: 1104,
        height: 832,
        label: '1104x832 (4:3)'
      }, {
        width: 1088,
        height: 896,
        label: '1088x896 (5:4)'
      }, {
        width: 1024,
        height: 1024,
        label: '1024x1024 (1:1)'
      }, {
        width: 896,
        height: 1088,
        label: '896x1088 (4:5)'
      }, {
        width: 832,
        height: 1104,
        label: '832x1104 (3:4)'
      }, {
        width: 832,
        height: 1216,
        label: '832x1216 (2:3)'
      }, {
        width: 768,
        height: 1344,
        label: '768x1344 (9:16)'
      }];
    }
    return currentModelCapabilities.supportedDimensions;
  }, [currentModelCapabilities]);

  // Detect if model uses aspectRatio only (no width/height support)
  const aspectRatioInput = useMemo(() => {
    return selectedImageModel?.inputs?.find(i => i.name === 'aspectRatio');
  }, [selectedImageModel]);
  const usesAspectRatioOnly = useMemo(() => {
    if (!selectedImageModel?.inputs) return false;
    const supportedParams = new Set(selectedImageModel.inputs.map(i => i.name));
    return supportedParams.has('aspectRatio') && !supportedParams.has('width') && !supportedParams.has('height');
  }, [selectedImageModel]);

  // Update image dimensions when model changes or when switching to image tab
  useEffect(() => {
    if (currentModelCapabilities && activeTab === 'image' && selectedImageModel) {
      const defaultDims = currentModelCapabilities.defaultDimensions;
      setImageWidth(defaultDims.width);
      setImageHeight(defaultDims.height);

      // Update guidance and steps to model defaults
      setImageGuidance(currentModelCapabilities.guidanceRange.default);
      setImageSamplingSteps(currentModelCapabilities.stepsRange.default);
    }
  }, [currentModelCapabilities, activeTab, selectedImageModel, setImageWidth, setImageHeight, setImageGuidance, setImageSamplingSteps]);

  // Group texture generations into sets (original + maps)
  const groupTextureGenerations = useCallback((generations: ScenarioAssetGeneration[]): GroupedTextureItem[] => {
    if (activeTab !== 'texture') {
      // Convert to grouped format for consistency
      return generations.map(gen => ({
        type: 'original' as const,
        generation: gen
      }));
    }
    const grouped: GroupedTextureItem[] = [];
    const processedJobIds = new Set<string>();

    // Process all generations - simpler approach
    generations.forEach((gen, index) => {
      if (processedJobIds.has(gen.jobId)) return;

      // Check if this generation contains both original texture and maps (from conversion)
      const hasOriginal = gen.assets.some(asset => asset.metadata?.type === 'texture');
      const hasMaps = gen.assets.some(asset => isTextureMap(asset));
      const hasOnlyOriginalTextures = gen.assets.every(asset => asset.metadata?.type === 'inference-txt2img-texture' || asset.metadata?.type === 'Text to Image' || asset.metadata?.type === 'text-to-image');

      // Skip detailed logging for cleaner output

      if (hasOriginal && hasMaps && gen.assets.length > 1) {
        // Found conversion set - create grouped item
        // This is a conversion result with original + maps in the same generation
        // Split them into separate items
        const textureAssets = gen.assets.filter(asset => asset.metadata?.type === 'texture');
        const mapAssets = gen.assets.filter(asset => isTextureMap(asset));

        // In a conversion set, there is typically 1 texture asset which is the flipbook cube preview
        // The "original" texture is not present in the conversion set data
        const flipbookCubeAsset = textureAssets.length > 0 ? textureAssets[0] : null;
        const originalAssets: ScenarioAsset[] = []; // No original texture in conversion set

        if (mapAssets.length > 0) {
          // Create a generation for display purposes (using the first map as representative)
          const firstMapAsset = mapAssets[0];
          const originalGeneration: ScenarioAssetGeneration = {
            ...gen,
            assets: firstMapAsset ? [firstMapAsset] : []
          };

          // Group maps by type to create separate generations for each map type
          const mapsByType = new Map<string, ScenarioAsset[]>();
          mapAssets.forEach(asset => {
            const mapType = getMapType(asset);
            if (!mapsByType.has(mapType)) {
              mapsByType.set(mapType, []);
            }
            mapsByType.get(mapType)!.push(asset);
          });

          // Create map generations
          const mapGenerations: ScenarioAssetGeneration[] = Array.from(mapsByType.entries()).map(([mapType, assets]) => ({
            ...gen,
            jobId: `${gen.jobId}-${mapType.toLowerCase()}`,
            assets: assets
          }));
          grouped.push({
            type: 'set',
            generation: originalGeneration,
            maps: mapGenerations,
            flipbookAsset: flipbookCubeAsset,
            expanded: false
          });
          processedJobIds.add(gen.jobId);
        } else {
          // Fallback to regular texture if something went wrong
          grouped.push({
            type: 'original',
            generation: gen
          });
          processedJobIds.add(gen.jobId);
        }
      } else {
        // For all other generations (regular textures, older textures, etc.)
        grouped.push({
          type: 'original',
          generation: gen
        });
        processedJobIds.add(gen.jobId);
      }
    });
    return grouped;
  }, [activeTab]);

  // State for expanded texture sets
  const [expandedSets, setExpandedSets] = useState<Set<string>>(new Set());

  // State for expandable sections
  const [promptExpanded, setPromptExpanded] = useState(true);
  const [referenceExpanded, setReferenceExpanded] = useState(true);
  const [settingsExpanded, setSettingsExpanded] = useState(true);

  // Get project directories with default generated folders
  const {
    projectAssetDirectories
  } = editorAssets;
  const directories = useMemo(() => {
    type DirectoryItem = {
      name: string;
      path: string;
      depth: number;
    };
    const dirs: DirectoryItem[] = projectAssetDirectories.map(dir => ({
      name: dir.name,
      path: dir.path,
      depth: dir.path.split('/').length - 1
    }));

    // Add default generated folders if they don't exist
    const defaultFolders = ['assets/generated/skybox', 'assets/generated/textures', 'assets/generated/images', 'assets/generated/mesh'];
    defaultFolders.forEach(folderPath => {
      if (!dirs.some(dir => dir.path === folderPath)) {
        const pathParts = folderPath.split('/');
        const name = pathParts[pathParts.length - 1];
        const newDir: DirectoryItem = {
          name,
          path: folderPath,
          depth: pathParts.length - 1
        };
        dirs.push(newDir);
      }
    });

    // Sort by path
    return dirs.sort((a, b) => a.path.localeCompare(b.path));
  }, [projectAssetDirectories]);

  /** Memoize expensive texture grouping - only compute when on texture tab */
  const groupedTextureItems = useMemo(() => {
    // Skip expensive grouping if not on texture tab
    if (activeTab !== 'texture') {
      return historicalGenerations.map(gen => ({
        type: 'original' as const,
        generation: gen
      }));
    }
    return groupTextureGenerations(historicalGenerations);
  }, [historicalGenerations, activeTab, groupTextureGenerations]);

  /** Dynamic validation for 3D generate button - checks required inputs based on model type */
  const is3DGenerateDisabled = useMemo(() => {
    if (!selectedModel3D) return true;
    const modelType = getModel3DType(selectedModel3D);
    if (modelType === 'txt23d') {
      // Text-to-3D: check for required string inputs (prompt)
      const requiredStringInputs = selectedModel3D.inputs?.filter(i => i.type === 'string' && i.required?.always) ?? [];
      // If no required string inputs defined, assume prompt is required
      if (requiredStringInputs.length === 0) {
        const promptInput = selectedModel3D.inputs?.find(i => i.type === 'string' && i.name.toLowerCase().includes('prompt'));
        if (promptInput) return !model3DParams[promptInput.name]?.toString().trim();
        return true; // No prompt input found, disable
      }
      return requiredStringInputs.some(input => !model3DParams[input.name]?.toString().trim());
    } else {
      // Image-to-3D: check for required file inputs
      const requiredFileInputs = selectedModel3D.inputs?.filter(i => i.type === 'file' && i.required?.always) ?? [];
      // If no required file inputs defined but model has file inputs, assume first is required
      if (requiredFileInputs.length === 0) {
        const fileInputs = selectedModel3D.inputs?.filter(i => i.type === 'file') ?? [];
        if (fileInputs.length > 0) return !model3DImages[fileInputs[0].name];
        // Fallback for models without inputs defined
        return !model3DImages.image;
      }
      return requiredFileInputs.some(input => !model3DImages[input.name]);
    }
  }, [selectedModel3D, model3DParams, model3DImages]);

  /** Disable reason for Image generate button - returns null if enabled */
  const imageGenerateDisableReason = useMemo(() => {
    if (isGenerating) return 'Currently generating...';
    if (!selectedImageModel) return 'Please select a model';
    if (isLoadingImageModelInputs) return 'Loading model parameters...';
    // inputs === undefined means not yet loaded, inputs === [] means loaded but no inputs (both are valid)
    if (selectedImageModel.inputs === undefined) return 'Model parameters not loaded';
    return null;
  }, [isGenerating, selectedImageModel, isLoadingImageModelInputs]);

  /** Disable reason for Texture generate button - returns null if enabled */
  const textureGenerateDisableReason = useMemo(() => {
    if (isGenerating) return 'Currently generating...';
    if (!selectedModel) return 'Please select a model';
    return null;
  }, [isGenerating, selectedModel]);

  /** Disable reason for 3D generate button - returns null if enabled */
  const model3DGenerateDisableReason = useMemo(() => {
    if (isGenerating) return 'Currently generating...';
    if (!selectedModel3D) return 'Please select a 3D model';
    const modelType = getModel3DType(selectedModel3D);
    if (modelType === 'txt23d') {
      const promptInput = selectedModel3D.inputs?.find(i => i.type === 'string' && i.name.toLowerCase().includes('prompt'));
      if (promptInput && !model3DParams[promptInput.name]?.toString().trim()) return 'Please enter a prompt';
    } else {
      const fileInputs = selectedModel3D.inputs?.filter(i => i.type === 'file') ?? [];
      if (fileInputs.length > 0 && !model3DImages[fileInputs[0].name]) return 'Please upload an image';
    }
    return null;
  }, [isGenerating, selectedModel3D, model3DParams, model3DImages]);

  // Helper functions to get current tab's values
  const getCurrentPrompt = () => {
    if (activeTab === 'skybox') return skyboxPrompt;
    if (activeTab === 'texture') return texturePrompt;
    if (activeTab === 'image') return imagePrompt;
    return ''; // 3D doesn't use prompts
  };
  const getCurrentReferenceImage = () => {
    if (activeTab === 'skybox') return skyboxReferenceImage;
    if (activeTab === 'texture') return textureReferenceImage;
    if (activeTab === 'image') return imageReferenceImage;
    return null; // 3D uses separate multi-view images
  };
  const getCurrentSaveFolder = () => {
    if (activeTab === 'skybox') return skyboxSaveFolder;
    if (activeTab === 'texture') return textureSaveFolder;
    if (activeTab === 'image') return imageSaveFolder;
    return threeDSaveFolder;
  };
  const setCurrentSaveFolder = (value: string) => {
    if (activeTab === 'skybox') {
      skyboxSaveFolderStorage.set(value);
    } else if (activeTab === 'texture') {
      textureSaveFolderStorage.set(value);
    } else if (activeTab === 'image') {
      imageSaveFolderStorage.set(value);
    } else {
      threeDSaveFolderStorage.set(value);
    }
  };
  const getCurrentPathPickerOpen = () => {
    if (activeTab === 'skybox') return skyboxPathPickerOpen;
    if (activeTab === 'texture') return texturePathPickerOpen;
    if (activeTab === 'image') return imagePathPickerOpen;
    return threeDPathPickerOpen;
  };
  const setCurrentPathPickerOpen = (value: boolean) => {
    if (activeTab === 'skybox') {
      setSkyboxPathPickerOpen(value);
    } else if (activeTab === 'texture') {
      setTexturePathPickerOpen(value);
    } else if (activeTab === 'image') {
      setImagePathPickerOpen(value);
    } else {
      setThreeDPathPickerOpen(value);
    }
  };
  const setCurrentPrompt = (value: string) => {
    if (activeTab === 'skybox') {
      setSkyboxPrompt(value);
    } else if (activeTab === 'texture') {
      setTexturePrompt(value);
    } else if (activeTab === 'image') {
      setImagePrompt(value);
    }
    // 3D doesn't use prompts
  };
  const setCurrentReferenceImage = (value: ReferenceImage | null) => {
    if (activeTab === 'skybox') {
      setSkyboxReferenceImage(value);
    } else if (activeTab === 'texture') {
      setTextureReferenceImage(value);
    } else if (activeTab === 'image') {
      setImageReferenceImage(value);
    }
    // 3D handles images separately through model3DImages

    // Auto-expand reference section when image is added
    if (value && !referenceExpanded) {
      setReferenceExpanded(true);
    }
  };

  /** Load historical generations with caching and race condition protection */
  const loadHistoricalGenerations = useCallback(async (forceRefresh = false) => {
    if (!firebaseUser) return;

    // Create unique request ID to track this specific load
    const requestId = `${activeTab}-${Date.now()}`;
    activeLoadRequestRef.current = requestId;
    const assetType = activeTab === 'skybox' ? 'skybox' : activeTab === 'texture' ? 'texture' : activeTab === 'image' ? 'image' : '3d';

    // Check if cache is still valid (within cache duration)
    const lastLoadTime = tabLoadTimestampRef.current.get(activeTab) ?? 0;
    const cacheAge = Date.now() - lastLoadTime;
    const isCacheValid = cacheAge < CACHE_DURATION_MS;

    // If not forcing refresh and cache is valid, skip fetch
    if (!forceRefresh && isCacheValid && lastLoadTime > 0) {
      if (DEBUG.tabSwitching) console.log(`✅ Tab ${activeTab} cache valid (${Math.round(cacheAge / 1000)}s old), skipping fetch`);
      setIsLoadingHistory(false); // CRITICAL: Reset loading state when skipping fetch
      return;
    }
    if (DEBUG.dataLoading) console.log(`🔄 Fetching ${assetType} assets (forceRefresh: ${forceRefresh}, cacheAge: ${Math.round(cacheAge / 1000)}s)`);
    setIsLoadingHistory(true);
    if (forceRefresh) {
      setHistoricalGenerations([]);
    }
    try {
      const response = await scenarioService.fetchUserAssets(firebaseUser.sub, assetType);

      // Check if this request is still the active one (user didn't switch tabs)
      if (activeLoadRequestRef.current !== requestId) {
        if (DEBUG.raceConditions) console.log(`⏭️ Discarding stale ${assetType} data, user switched tabs`);
        setIsLoadingHistory(false); // Reset loading state for discarded requests
        return;
      }
      const generations = AssetFilters.groupByGeneration(response.assets ?? []);
      if (DEBUG.dataLoading) console.log(`📚 Loaded ${generations.length} fresh ${assetType} generations (${response.assets?.length ?? 0} assets)`);

      // Update both the current state and the cache atomically
      setHistoricalGenerations(generations);
      setHistoricalGenerationsCache(prev => ({
        ...prev,
        [activeTab]: generations
      }));

      // Update timestamp to mark cache as fresh
      tabLoadTimestampRef.current.set(activeTab, Date.now());
      setError(null);
    } catch (error) {
      // Only handle error if this is still the active request
      if (activeLoadRequestRef.current !== requestId) {
        setIsLoadingHistory(false); // Reset loading state for discarded error requests
        return;
      }
      console.error('❌ Failed to load historical generations:', error);
      setError('Unable to load recent generations. The Scenario API may be temporarily unavailable.');
    } finally {
      // Only update loading state if this is still the active request
      if (activeLoadRequestRef.current === requestId) {
        setIsLoadingHistory(false);
      }
    }
  }, [activeTab, firebaseUser, CACHE_DURATION_MS]);

  /** Optimized tab switching with instant cache display and debounced loading */
  useEffect(() => {
    if (!isOpen) return;

    // Clear any pending debounced loads
    if (tabSwitchTimeoutRef.current) {
      clearTimeout(tabSwitchTimeoutRef.current);
    }

    // Check if we have a valid cached timestamp
    const lastLoadTime = tabLoadTimestampRef.current.get(activeTab) ?? 0;
    const cacheAge = Date.now() - lastLoadTime;
    const isCacheValid = cacheAge < CACHE_DURATION_MS && lastLoadTime > 0;

    // Immediately show cached data if we have it AND it has actual data
    const cachedData = historicalGenerationsCache[activeTab];
    const hasCachedData = cachedData !== undefined && cachedData.length > 0;
    if (hasCachedData && isCacheValid) {
      // Cache is valid and has data - show it immediately without loading
      setHistoricalGenerations(cachedData);
      setIsLoadingHistory(false);
      if (DEBUG.tabSwitching) console.log(`⚡ Instantly displayed ${cachedData.length} cached ${activeTab} generations (${Math.round(cacheAge / 1000)}s old)`);
    } else {
      // No valid cache OR empty cache - show empty state and trigger load
      setHistoricalGenerations([]);
      setIsLoadingHistory(true);
      if (DEBUG.tabSwitching) {
        if (!hasCachedData) {
          console.log(`🔄 No cached data for ${activeTab}, loading fresh data`);
        } else {
          console.log(`🔄 Cache expired for ${activeTab} (${Math.round(cacheAge / 1000)}s old), loading fresh data`);
        }
      }

      // Debounce the load to handle rapid tab switches
      tabSwitchTimeoutRef.current = setTimeout(() => {
        loadHistoricalGenerations();
      }, 50);
    }

    // Clear thumbnails when switching tabs (but not while generating or polling)
    // Keep thumbnails visible during polling to prevent flashing empty state
    if (!isGenerating && !isPollingForAssets) {
      setThumbnails([]);
    }

    // Cleanup
    return () => {
      if (tabSwitchTimeoutRef.current) {
        clearTimeout(tabSwitchTimeoutRef.current);
      }
    };
    // NOTE: historicalGenerationsCache is intentionally NOT in deps to avoid infinite loops
    // The cache is only read, not written, in this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, isGenerating, isPollingForAssets, loadHistoricalGenerations, CACHE_DURATION_MS]);

  /** Cleanup on unmount - cancel any pending requests and stop polling */
  useEffect(() => {
    return () => {
      activeLoadRequestRef.current = null;
      if (tabSwitchTimeoutRef.current) {
        clearTimeout(tabSwitchTimeoutRef.current);
      }
      // Stop polling when component unmounts
      pollingStoppedRef.current = true;
      setIsPollingForAssets(false);
    };
  }, []);

  /** Stop polling when dialog closes */
  useEffect(() => {
    if (!isOpen) {
      pollingStoppedRef.current = true;
      if (isPollingForAssets) {
        setIsPollingForAssets(false);
      }
    }
  }, [isOpen, isPollingForAssets]);

  // Auto-load models and select default when switching to texture tab
  useEffect(() => {
    if (activeTab === 'texture' && isOpen) {
      const loadModelsForDefaultSelection = async () => {
        try {
          const textureModels = await loadTextureModels();
          if (textureModels && textureModels.length > 0 && !selectedModel) {
            const storedPrefs = loadScenarioPrefs();
            let defaultModel: ScenarioImageModel | undefined;

            // First priority: Stored preference (if valid)
            if (storedPrefs.selectedTextureModelId) {
              defaultModel = textureModels.find(m => m.id === storedPrefs.selectedTextureModelId);
            }

            // Second priority: Latest featured model
            if (!defaultModel) {
              defaultModel = textureModels.find(m => m.featured || m.tags?.includes('sc:featured'));
            }

            // Fallback: Match by name from preferences
            if (!defaultModel) {
              const prefs = DefaultModelPreferences.texture;
              defaultModel = textureModels.find(m => prefs.fallbackNames.some(name => m.name.toLowerCase().includes(name.toLowerCase())));
            }

            // Last resort: First model
            if (!defaultModel) defaultModel = textureModels[0];
            if (defaultModel) setSelectedModel(defaultModel as ScenarioModel);
          }
        } catch (error) {
          console.error('Failed to load default model:', error);
        }
      };
      loadModelsForDefaultSelection();
    }
  }, [activeTab, isOpen, selectedModel]);

  // Auto-load skybox styles when switching to skybox tab
  useEffect(() => {
    if (activeTab === 'skybox' && isOpen && !selectedSkyboxStyle) {
      const storedPrefs = loadScenarioPrefs();
      const storedStyle = storedPrefs.selectedSkyboxStyle;
      // Validate stored style exists in SkyboxStyleCategories
      const validStyles = Object.values(SkyboxStyleCategories).flatMap(cat => cat.styles.map(s => s.value));
      setSelectedSkyboxStyle(storedStyle && validStyles.includes(storedStyle) ? storedStyle : 'standard');
    }
  }, [activeTab, isOpen, selectedSkyboxStyle]);

  // Auto-load image models and select default when switching to image tab
  useEffect(() => {
    if (activeTab === 'image' && isOpen) {
      const loadImageModelsForDefaultSelection = async () => {
        try {
          const imageModels = await scenarioClient.getImageModels();
          if (imageModels.length > 0 && !selectedImageModel) {
            const storedPrefs = loadScenarioPrefs();
            let defaultModel: ScenarioImageModel | undefined;

            // First priority: Stored preference (if valid)
            if (storedPrefs.selectedImageModelId) {
              defaultModel = imageModels.find((m: ScenarioImageModel) => m.id === storedPrefs.selectedImageModelId);
            }

            // Second priority: Latest featured model
            if (!defaultModel) {
              defaultModel = imageModels.find((m: ScenarioImageModel) => m.featured || m.tags?.includes('sc:featured'));
            }

            // Fallback: Match by name from preferences
            if (!defaultModel) {
              const prefs = DefaultModelPreferences.image;
              defaultModel = imageModels.find((m: ScenarioImageModel) => prefs.fallbackNames.some(name => m.name.toLowerCase().includes(name.toLowerCase())));
            }

            // Last resort: First model
            if (!defaultModel) defaultModel = imageModels[0];
            if (defaultModel) {
              const modelWithImage = {
                ...defaultModel
              };
              if (defaultModel.thumbnail?.url) {
                modelWithImage.images = [{
                  id: defaultModel.thumbnail.assetId || defaultModel.thumbnail.id || `thumb-${defaultModel.id}`,
                  url: defaultModel.thumbnail.url
                }];
              }

              // Set model first (without inputs) for UI responsiveness
              setSelectedImageModel(modelWithImage);
              setIsLoadingImageModelInputs(true);
              try {
                // Fetch model inputs lazily
                const inputs = await scenarioClient.getModelInputs(defaultModel.id);
                if (inputs) {
                  // Update model with inputs
                  const updatedModel = {
                    ...modelWithImage,
                    inputs,
                    supportedParams: inputs.map(i => i.name)
                  };
                  setSelectedImageModel(prev => prev?.id === defaultModel!.id ? updatedModel : prev);
                  // Initialize dynamic params for this model
                  initializeImageModelParams(updatedModel);
                }
              } finally {
                setIsLoadingImageModelInputs(false);
              }
            }
          }
        } catch (error) {
          console.error('Failed to load default image model:', error);
          setIsLoadingImageModelInputs(false);
        }
      };
      loadImageModelsForDefaultSelection();
    }
  }, [activeTab, isOpen, selectedImageModel, initializeImageModelParams]);

  // Auto-load 3D models and select default when switching to 3D tab
  useEffect(() => {
    if (activeTab === '3d' && isOpen) {
      const loadModel3DForDefaultSelection = async () => {
        try {
          const model3DList = await scenarioClient.get3DModels();
          if (model3DList.length > 0 && !selectedModel3D) {
            const validModels = model3DList.filter(m => m && m.name);
            const storedPrefs = loadScenarioPrefs();
            let defaultModel: ScenarioModel3D | undefined;

            // First priority: Stored preference (if valid)
            if (storedPrefs.selectedModel3DId) {
              defaultModel = validModels.find(m => m.id === storedPrefs.selectedModel3DId);
            }

            // Second priority: Latest featured model
            if (!defaultModel) {
              defaultModel = validModels.find(m => m.featured || m.tags?.includes('sc:featured'));
            }

            // Fallback: Match by ID from preferences
            if (!defaultModel) {
              const prefs = DefaultModelPreferences.model3D;
              defaultModel = validModels.find(m => m.id === prefs.fallbackId);
            }

            // Fallback: Match by name from preferences
            if (!defaultModel) {
              const prefs = DefaultModelPreferences.model3D;
              defaultModel = validModels.find(m => prefs.fallbackNames.some(name => m.name.toLowerCase().includes(name.toLowerCase())));
            }

            // Last resort: First model (already sorted by API)
            if (!defaultModel) defaultModel = validModels[0];
            if (defaultModel) {
              setSelectedModel3D(defaultModel);
              initializeModel3DParams(defaultModel);
            }
          }
        } catch (error) {
          console.error('Failed to load default 3D model:', error);
        }
      };
      loadModel3DForDefaultSelection();
    }
  }, [activeTab, isOpen, selectedModel3D]);
  const handleGenerate = async () => {
    // Validate based on tab type
    if (activeTab === '3d') {
      if (!selectedModel3D) {
        setError('Please select a 3D model');
        return;
      }
      const modelType = getModel3DType(selectedModel3D);
      if (modelType === 'txt23d') {
        // Text-to-3D: validate required string inputs (prompt)
        const requiredStringInputs = selectedModel3D?.inputs?.filter(input => input.type === 'string' && input.required?.always) || [];
        for (const input of requiredStringInputs) {
          if (!model3DParams[input.name]?.toString().trim()) {
            setError(`Please enter ${input.label.toLowerCase()} for 3D generation`);
            return;
          }
        }

        // Fallback: check for any string input with 'prompt' in name
        if (requiredStringInputs.length === 0) {
          const promptInput = selectedModel3D?.inputs?.find(i => i.type === 'string' && i.name.toLowerCase().includes('prompt'));
          if (promptInput && !model3DParams[promptInput.name]?.toString().trim()) {
            setError('Please enter a prompt for 3D generation');
            return;
          }
        }
      } else {
        // Image-to-3D: validate required file inputs
        const requiredImageInputs = selectedModel3D?.inputs?.filter(input => input.type === 'file' && input.required?.always) || [];
        for (const input of requiredImageInputs) {
          if (!model3DImages[input.name]) {
            setError(`Please upload ${input.label.toLowerCase()} for 3D generation`);
            return;
          }
        }

        // Fallback check for models without inputs defined
        if (!selectedModel3D?.inputs && !model3DImages.image) {
          setError('Please upload a front view image for 3D generation');
          return;
        }
      }
    } else {
      if (!getCurrentPrompt().trim()) {
        setError('Please enter a prompt');
        return;
      }
      if (activeTab === 'texture' && !selectedModel) {
        setError('Please select a model for texture generation');
        return;
      }
      if (activeTab === 'image') {
        if (!selectedImageModel) {
          setError('Please select a model for image generation');
          return;
        }
        if (isLoadingImageModelInputs) {
          setError('Please wait for model parameters to finish loading');
          return;
        }
        if (!selectedImageModel.inputs) {
          // Inputs not loaded - this shouldn't happen but guard against it
          setError('Model parameters not loaded. Please reselect the model.');
          return;
        }
      }
    }
    if (!firebaseUser) {
      setError('Please log in to generate content');
      return;
    }
    setIsGenerating(true);
    setError(null);

    // Clear any existing thumbnails first
    setThumbnails([]);

    // Safety timeout to reset generating state after 15 minutes
    const safetyTimeout = setTimeout(() => {
      setIsGenerating(false);
      setError('Generation timed out - please try again');
    }, 15 * 60 * 1000);
    const count = activeTab === 'skybox' ? skyboxCount : activeTab === 'texture' ? textureCount : activeTab === 'image' ? imageCount : 1; // 3D generates 1 model

    // Create thumbnail placeholders
    const newThumbnails: GenerationThumbnail[] = Array.from({
      length: count
    }, (_, index) => ({
      id: `${activeTab}-${index}`,
      status: 'queued',
      progress: 0,
      imageUrl: null,
      assetId: null
    }));
    setThumbnails(newThumbnails);
    try {
      let result;
      if (activeTab === 'skybox') {
        const params: ScenarioSkyboxParams = {
          prompt: getCurrentPrompt().trim(),
          style: selectedSkyboxStyle || undefined,
          seed: skyboxSeed?.toString(),
          numOutputs: skyboxCount,
          numInferenceSteps: skyboxSamplingSteps,
          geometryEnforcement: geometryEnforcement,
          referenceImage: getCurrentReferenceImage() || undefined
        };
        result = await scenarioService.generateSkybox(params, (progress, status) => {
          setThumbnails(prev => prev.map(t => ({
            ...t,
            progress,
            status: status === 'Completed' ? 'generating' :
            // Keep as generating until we download
            status && (status.includes('Generating') || status.includes('%')) ? 'generating' : progress > 0 ? 'generating' : 'queued'
          })));
        }, firebaseUser.sub);
      } else if (activeTab === 'texture') {
        const params: ScenarioTextureParams = {
          modelId: selectedModel!.id,
          prompt: getCurrentPrompt().trim(),
          seed: textureSeed,
          numSamples: textureCount,
          numInferenceSteps: textureSamplingSteps,
          guidance: guidance,
          width: textureWidth,
          height: textureHeight,
          tiling: tiling,
          seamless: seamless,
          referenceImage: getCurrentReferenceImage() || undefined
        };
        result = await scenarioService.generateTexture(params, (progress, status) => {
          setThumbnails(prev => prev.map(t => ({
            ...t,
            progress,
            status: status === 'Completed' ? 'generating' : status && (status.includes('Generating') || status.includes('%')) ? 'generating' : progress > 0 ? 'generating' : 'queued'
          })));
        }, firebaseUser.sub);
      } else if (activeTab === 'image') {
        // Image generation
        const params: ScenarioImageParams = {
          modelId: selectedImageModel!.id,
          modelType: selectedImageModel!.type,
          modelName: selectedImageModel!.name,
          modelInputs: selectedImageModel!.inputs,
          prompt: getCurrentPrompt().trim(),
          seed: imageSeed,
          numSamples: imageCount,
          numInferenceSteps: imageSamplingSteps,
          guidance: imageGuidance,
          width: imageWidth,
          height: imageHeight,
          aspectRatio: usesAspectRatioOnly ? imageAspectRatio : undefined,
          // Direct aspectRatio for models that use it
          referenceImage: getCurrentReferenceImage() || undefined,
          dynamicParams: imageModelParams // Model-specific params (resolution, useGoogleSearch, etc.)
        };
        result = await scenarioService.generateImage(params, (progress, status) => {
          setThumbnails(prev => prev.map(t => ({
            ...t,
            progress,
            status: status === 'Completed' ? 'generating' : status && (status.includes('Generating') || status.includes('%')) ? 'generating' : progress > 0 ? 'generating' : 'queued'
          })));
        }, firebaseUser.sub);
      } else {
        // 3D generation - build params dynamically from model inputs based on model type
        const modelType = getModel3DType(selectedModel3D);
        const params: ScenarioModel3DParams = {
          modelId: selectedModel3D!.id,
          image: '',
          // Will be set below based on model type
          ...model3DParams // Spread dynamic parameters (includes prompt for txt23d)
        };
        if (modelType === 'txt23d') {
          // Text-to-3D: no image required, prompt is in model3DParams
          // Some txt23d models may still accept optional reference image
          const fileInputs = selectedModel3D?.inputs?.filter(i => i.type === 'file') ?? [];
          if (fileInputs.length > 0 && model3DImages[fileInputs[0].name]) {
            params.image = model3DImages[fileInputs[0].name]!.id;
          }
        } else {
          // Image-to-3D: get primary image from file inputs or fallback
          const fileInputs = selectedModel3D?.inputs?.filter(i => i.type === 'file') ?? [];
          if (fileInputs.length > 0 && model3DImages[fileInputs[0].name]) {
            params.image = model3DImages[fileInputs[0].name]!.id;
          } else if (model3DImages.image) {
            params.image = model3DImages.image.id;
          }

          // Add multi-view images if available (img23d specific)
          if (model3DImages.backImage) params.backImage = model3DImages.backImage.id;
          if (model3DImages.leftImage) params.leftImage = model3DImages.leftImage.id;
          if (model3DImages.rightImage) params.rightImage = model3DImages.rightImage.id;
        }
        result = await scenarioService.generateModel3D(params, (progress, status) => {
          setThumbnails(prev => prev.map(t => ({
            ...t,
            progress,
            status: status === 'Completed' ? 'generating' : status && (status.includes('Generating') || status.includes('%')) ? 'generating' : progress > 0 ? 'generating' : 'queued'
          })));
        }, firebaseUser.sub);
      }
      if (result.status === 'success' && result.images && result.images.length > 0) {
        // Download all assets
        for (let i = 0; i < result.images.length; i++) {
          const assetId = result.images[i];
          try {
            const blob = await scenarioClient.downloadAsset(assetId);

            // Less strict validation - just check if it's a valid blob
            if (!blob || blob.size === 0) {
              throw new Error(`Downloaded asset is empty (asset ID: ${assetId}, type: ${activeTab})`);
            }
            const imageUrl = URL.createObjectURL(blob);
            setThumbnails(prev => prev.map((t, index) => index === i ? {
              ...t,
              status: 'completed' as const,
              progress: 100,
              imageUrl,
              assetId
            } : t));
          } catch (downloadError) {
            console.error(`Download failed for ${activeTab} ${i}:`, downloadError);
            setThumbnails(prev => prev.map((t, index) => index === i ? {
              ...t,
              status: 'error',
              progress: 0
            } : t));
            // Don't set global error for individual download failures
            console.error(`Failed to download asset ${i}:`, downloadError);
          }
        }

        // Refresh historical generations after successful completion
        // Invalidate cache timestamp but keep data visible to prevent flashing
        if (DEBUG.cacheOperations) console.log(`🔄 Invalidating ${activeTab} cache after generation`);
        tabLoadTimestampRef.current.delete(activeTab); // Invalidate timestamp to trigger reload
        // Note: We don't clear historicalGenerationsCache here to prevent empty state flashing
        // The polling will update it with fresh data including the new asset

        // Start polling for new assets (backend processing may take time)
        setIsPollingForAssets(true);
        pollingStoppedRef.current = false; // Reset polling flag
        const currentTab = activeTab; // Capture current tab value to prevent stale closure

        const pollForNewAssets = async (attempts = 0, maxAttempts = 10) => {
          try {
            if (pollingStoppedRef.current || attempts >= maxAttempts) {
              if (attempts >= maxAttempts) {
                console.log('⏱️ Stopped polling for new assets after', maxAttempts, 'attempts');
              }
              setIsPollingForAssets(false);
              return;
            }
            const delay = currentTab === '3d' ? 2000 : 1000; // 2s for 3D, 1s for others
            await new Promise(resolve => setTimeout(resolve, delay));
            if (pollingStoppedRef.current) {
              setIsPollingForAssets(false);
              return;
            }
            await loadHistoricalGenerations(true);

            // Continue polling if we haven't reached max attempts
            if (attempts < 3) {
              // Poll aggressively for first 3 attempts
              pollForNewAssets(attempts + 1, maxAttempts);
            } else {
              setIsPollingForAssets(false);
            }
          } catch (error) {
            console.error('❌ Polling error:', error);
            setIsPollingForAssets(false);
            pollingStoppedRef.current = true;
          }
        };
        pollForNewAssets();
      } else {
        // Build detailed error message from result context
        const contextParts: string[] = [];
        if (result.context?.generationType) contextParts.push(`Type: ${result.context.generationType}`);
        if (result.context?.modelName) contextParts.push(`Model: ${result.context.modelName}`);else if (result.context?.modelId) contextParts.push(`Model ID: ${result.context.modelId}`);
        if (result.context?.prompt) contextParts.push(`Prompt: "${result.context.prompt.slice(0, 50)}${result.context.prompt.length > 50 ? '...' : ''}"`);
        if (result.jobId) contextParts.push(`Job ID: ${result.jobId}`);
        const contextStr = contextParts.length > 0 ? `\n${contextParts.join('\n')}` : '';
        const apiError = result.error ? `\nAPI Error: ${result.error}` : '';
        throw new Error(`Generation failed - no images returned.${apiError}${contextStr}`);
      }
    } catch (error) {
      console.error('Generation error:', error);

      // Format error message with context if available
      let errorMessage = 'Generation failed';
      if (error instanceof ScenarioAPIError) {
        errorMessage = error.getDetailedMessage();
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = `Generation failed: ${String(error)}`;
      }
      setError(errorMessage);
      setThumbnails(prev => prev.map(t => ({
        ...t,
        status: 'error' as const,
        progress: 0
      })));
    } finally {
      clearTimeout(safetyTimeout);
      setIsGenerating(false);
    }
  };
  const handleEnhancePrompt = async () => {
    if (!firebaseUser) {
      setError('Please log in to enhance prompts');
      return;
    }

    // Clear any previous magic prompt errors
    clearMagicPromptError();
    setError(null);
    try {
      // Get current model for context
      const currentModel = (() => {
        switch (activeTab as GenerationType) {
          case 'texture':
            return selectedModel;
          case 'image':
            return selectedImageModel;
          case '3d':
            return selectedModel3D;
          case 'skybox':
            return null;
          default:
            return null;
        }
      })();
      const result = await enhanceMagicPrompt({
        activeTab,
        currentPrompt: getCurrentPrompt(),
        referenceImage: getCurrentReferenceImage(),
        selectedModel: currentModel
      });

      // Update the prompt with the enhanced version
      setCurrentPrompt(result.enhancedPrompt);
      console.log('🎉 Magic Prompt Applied:', {
        mode: result.mode,
        improvement: `${result.originalPrompt.length} → ${result.enhancedPrompt.length} chars`,
        hadModelContext: result.hadModelContext,
        hadImageContext: result.hadImageContext
      });
    } catch (error) {
      console.error('Prompt enhancement error:', error);
      setError(magicPromptError || `Prompt enhancement failed: ${error}`);
    }
  };
  const handleSaveToProject = async () => {
    const completedThumbnails = thumbnails.filter(t => t.status === 'completed' && t.assetId);
    if (completedThumbnails.length === 0) {
      setError('No completed generations to save');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const assetIds = completedThumbnails.map(t => t.assetId!);
      const saveFolder = getCurrentSaveFolder();
      await scenarioService.saveAssetsToProject(assetIds, activeTab, saveFolder);

      // Show success toast
      const assetType = activeTab === 'skybox' ? 'skybox' : activeTab === 'texture' ? 'texture' : activeTab === '3d' ? '3D model' : 'image';
      const assetTypePlural = activeTab === 'skybox' ? 'skyboxes' : activeTab === 'texture' ? 'textures' : activeTab === '3d' ? '3D models' : 'images';
      toast.success(`✅ Saved ${assetIds.length} ${assetIds.length === 1 ? assetType : assetTypePlural}`, {
        description: `Location: ${saveFolder}`,
        duration: 4000
      });

      // Clear thumbnails after successful save
      setThumbnails([]);
    } catch (error) {
      console.error('Save to project failed:', error);
      setError(`Save failed: ${error}`);
      toast.error('❌ Failed to save assets', {
        description: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveImageToProject = async (assetId: string, thumbnailId: string) => {
    setIsSaving(true);
    setError(null);
    try {
      const saveFolder = getCurrentSaveFolder();
      await scenarioService.saveAssetsToProject([assetId], activeTab, saveFolder);

      // Show success toast
      const assetType = activeTab === 'skybox' ? 'skybox' : activeTab === 'texture' ? 'texture' : activeTab === '3d' ? '3D model' : 'image';
      toast.success(`✅ Saved ${assetType} to project`, {
        description: `Location: ${saveFolder}`,
        duration: 4000
      });
    } catch (error) {
      console.error('Save to project failed:', error);
      setError(`Save failed: ${error}`);
      toast.error('❌ Failed to save asset', {
        description: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveTextureSet = async (maps: ScenarioAssetGeneration[]) => {
    setIsSaving(true);
    setError(null);
    try {
      // Collect all asset IDs from all map generations
      const assetIds = maps.flatMap(mapGen => mapGen.assets.map(asset => asset.id));
      if (assetIds.length === 0) {
        setError('No texture maps to save');
        return;
      }
      const saveFolder = getCurrentSaveFolder();
      await scenarioService.saveAssetsToProject(assetIds, activeTab, saveFolder);

      // Show success toast
      toast.success(`✅ Saved ${assetIds.length} texture maps`, {
        description: `Location: ${saveFolder}`,
        duration: 4000
      });
    } catch (error) {
      console.error('Save texture set failed:', error);
      setError(`Save failed: ${error}`);
      toast.error('❌ Failed to save texture maps', {
        description: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleUpscaleImage = async (assetId: string) => {
    if (activeTab !== 'skybox' && activeTab !== 'image') {
      setError('Upscaling is only available for skyboxes and images');
      return;
    }
    setIsUpscaling(true);
    setError(null);

    // Create a thumbnail for the upscaling job
    const upscaleThumbnailId = `upscale-${assetId}-${Date.now()}`;
    const newThumbnail: GenerationThumbnail = {
      id: upscaleThumbnailId,
      status: 'queued',
      progress: 0,
      imageUrl: null,
      assetId: null
    };

    // Add to thumbnails array to show progress
    setThumbnails(prev => [...prev, newThumbnail]);
    try {
      // Start upscaling with scaling factor 2
      const result = await scenarioService.upscaleSkybox(assetId, 2,
      // scaling factor
      (progress, status) => {
        // Update thumbnail progress in real-time
        setThumbnails(prev => prev.map(thumb => thumb.id === upscaleThumbnailId ? {
          ...thumb,
          status: progress === 100 ? 'completed' : 'generating',
          progress
        } : thumb));
      }, firebaseUser?.sub);
      if (result.status === 'success' && result.images.length > 0) {
        // Get the upscaled image URL
        const upscaledAssetId = result.images[0];
        const upscaledAsset = await scenarioClient.getAsset(upscaledAssetId);

        // Update the thumbnail with the final image
        setThumbnails(prev => prev.map(thumb => thumb.id === upscaleThumbnailId ? {
          ...thumb,
          status: 'completed',
          progress: 100,
          imageUrl: upscaledAsset.url,
          assetId: upscaledAssetId
        } : thumb));

        // Refresh historical generations to show the new upscaled image
        if (DEBUG.cacheOperations) console.log(`🔄 Invalidating ${activeTab} cache after upscale`);
        tabLoadTimestampRef.current.delete(activeTab);
        // Keep cache data visible during polling to prevent flashing

        // Poll for the upscaled asset (may take time to process)
        setIsPollingForAssets(true);
        pollingStoppedRef.current = false;
        const pollForUpscaledAsset = async (attempts = 0) => {
          try {
            if (pollingStoppedRef.current || attempts >= 5) {
              setIsPollingForAssets(false);
              return;
            }
            await new Promise(resolve => setTimeout(resolve, 1500));
            if (pollingStoppedRef.current) {
              setIsPollingForAssets(false);
              return;
            }
            await loadHistoricalGenerations(true);
            if (attempts < 2) {
              pollForUpscaledAsset(attempts + 1);
            } else {
              setIsPollingForAssets(false);
            }
          } catch (error) {
            console.error('❌ Upscale polling error:', error);
            setIsPollingForAssets(false);
            pollingStoppedRef.current = true;
          }
        };
        pollForUpscaledAsset();
      } else {
        // Mark thumbnail as failed
        setThumbnails(prev => prev.map(thumb => thumb.id === upscaleThumbnailId ? {
          ...thumb,
          status: 'error'
        } : thumb));
        const apiError = result.error ? `: ${result.error}` : ' - no upscaled image returned';
        const jobInfo = result.jobId ? ` (job ID: ${result.jobId})` : '';
        setError(`Failed to upscale skybox${apiError}${jobInfo}`);
      }
    } catch (error) {
      console.error('Upscaling failed:', error);
      // Mark thumbnail as failed
      setThumbnails(prev => prev.map(thumb => thumb.id === upscaleThumbnailId ? {
        ...thumb,
        status: 'error'
      } : thumb));
      const errorMsg = error instanceof Error ? error.message : String(error);
      setError(`Upscaling failed: ${errorMsg}`);
    } finally {
      setIsUpscaling(false);
    }
  };
  const handleRemoveBackground = async (assetId: string) => {
    if (!firebaseUser) {
      setError('Please log in to remove backgrounds');
      return;
    }
    setIsUpscaling(true); // Reuse upscaling state for loading
    setError(null);
    try {
      // Track progress
      const updateProgress = (progress: number, status?: string) => {
        // Progress tracking for background removal
      };

      // Call the service method which handles the API call and polling
      const result = await scenarioService.removeBackground(assetId, updateProgress, firebaseUser.sub);
      if (result.status === 'success' && result.images && result.images.length > 0) {
        // Refresh historical generations to show the new processed image
        if (DEBUG.cacheOperations) console.log(`🔄 Invalidating ${activeTab} cache after background removal`);
        tabLoadTimestampRef.current.delete(activeTab);
        // Keep cache data visible during polling to prevent flashing

        // Poll for the processed asset
        setIsPollingForAssets(true);
        pollingStoppedRef.current = false;
        const pollForProcessedAsset = async (attempts = 0) => {
          try {
            if (pollingStoppedRef.current || attempts >= 5) {
              setIsPollingForAssets(false);
              return;
            }
            await new Promise(resolve => setTimeout(resolve, 1500));
            if (pollingStoppedRef.current) {
              setIsPollingForAssets(false);
              return;
            }
            await loadHistoricalGenerations(true);
            if (attempts < 2) {
              pollForProcessedAsset(attempts + 1);
            } else {
              setIsPollingForAssets(false);
            }
          } catch (error) {
            console.error('❌ Background removal polling error:', error);
            setIsPollingForAssets(false);
            pollingStoppedRef.current = true;
          }
        };
        pollForProcessedAsset();
      } else {
        const apiError = result.error ? `: ${result.error}` : ' - no processed image returned';
        const jobInfo = result.jobId ? ` (job ID: ${result.jobId})` : '';
        throw new Error(`Background removal failed${apiError}${jobInfo}`);
      }
    } catch (error) {
      console.error('❌ Background removal failed:', error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      setError(errorMsg);
    } finally {
      setIsUpscaling(false);
    }
  };
  const handleConvertTexture = async (assetId: string) => {
    if (!firebaseUser) {
      setError('Please log in to convert textures');
      return;
    }
    setIsConverting(true);
    setError(null);
    try {
      const updateProgress = (progress: number, status: string) => {
        // Update progress for the converting asset
        setHistoricalGenerations(prev => prev.map(gen => ({
          ...gen,
          assets: gen.assets.map(asset => asset.id === assetId ? {
            ...asset,
            progress,
            status: status === 'Completed' ? 'completed' : 'generating'
          } : asset)
        })));
      };
      const result = await scenarioService.convertTexture({
        sourceAssetId: assetId,
        // Use default values for now - we can add UI controls later
        raised: 0.35,
        shiny: 0.6,
        polished: 0.66,
        angular: 0.5,
        invert: false
      }, updateProgress, firebaseUser.sub);
      if (result.status === 'success') {
        // Refresh historical generations to show new texture maps
        if (DEBUG.cacheOperations) console.log(`🔄 Invalidating ${activeTab} cache after texture conversion`);
        tabLoadTimestampRef.current.delete(activeTab);
        // Keep cache data visible during polling to prevent flashing

        // Poll for the converted texture maps (conversion takes time)
        setIsPollingForAssets(true);
        pollingStoppedRef.current = false;
        const pollForConvertedTexture = async (attempts = 0) => {
          try {
            if (pollingStoppedRef.current || attempts >= 5) {
              setIsPollingForAssets(false);
              return;
            }
            await new Promise(resolve => setTimeout(resolve, 2000)); // 2s for texture conversion

            if (pollingStoppedRef.current) {
              setIsPollingForAssets(false);
              return;
            }
            await loadHistoricalGenerations(true);
            if (attempts < 2) {
              pollForConvertedTexture(attempts + 1);
            } else {
              setIsPollingForAssets(false);
            }
          } catch (error) {
            console.error('❌ Texture conversion polling error:', error);
            setIsPollingForAssets(false);
            pollingStoppedRef.current = true;
          }
        };
        pollForConvertedTexture();
      } else {
        setError('Failed to convert texture');
      }
    } catch (error) {
      console.error('Texture conversion failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to convert texture');
    } finally {
      setIsConverting(false);
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen && e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onOpenChange]);
  return <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[1400px]! min-w-[800px] h-[85dvh] flex flex-col"
    // onInteractOutside={e => e.preventDefault()}
    >
        <DialogHeader>
          <DialogTitle>
            <span className="flex items-center gap-2">
              <WandSparklesIcon className="size-4" />
              Foundry - AI Generation
            </span>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={value => setActiveTab(value as 'skybox' | 'texture' | 'image' | '3d')}
      // className="-mt-4 flex flex-col flex-1 overflow-hidden" // negative margin for now as there's no description
      className="flex flex-col flex-1 overflow-hidden" // negative margin for now as there's no description
      >
          {/* Tab Navigation */}
          <div className="flex items-center mb-4">
            <TabsList className="gap-1.5  w-full">
              <TabsTrigger value="skybox" className="w-[280px]">
                Skyboxes
              </TabsTrigger>
              <TabsTrigger value="texture">
                Textures
              </TabsTrigger>
              <TabsTrigger value="image">
                Images
              </TabsTrigger>
              <TabsTrigger value="3d">
                3D
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Content */}
          <TabsContent value={activeTab} className="flex flex-1 text-xs overflow-hidden mx- rounded-lg border">
            {/* Left Panel - Controls */}
            <div className="w-[480px]  flex flex-col overflow-hidden bg-input-idle">
              <div className="p-3 pr-3 m-0 space-y-2 overflow-y-scroll flex-1">
                {/* Model Selection Section - Only for tabs that need it */}
                {(activeTab === 'texture' || activeTab === 'image' || activeTab === '3d') && <Collapsible defaultOpen={true} className="p-1 rounded-lg bg-background/50 shadow-sm">
                    <CollapsibleTrigger asChild>
                      <Button variant="transparent" className="w-full justify-between font-medium">
                        <span>Model</span>
                        <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-2">
                      {activeTab === 'texture' && <TextureModelSelector selectedModel={selectedModel} onModelSelect={model => {
                    setSelectedModel(model);
                    saveScenarioPrefs({
                      selectedTextureModelId: model.id
                    });
                    console.log('🎨 Texture model selected:', {
                      id: model.id,
                      name: model.name,
                      type: model.type
                    });
                  }} disabled={isGenerating} />}
                      {activeTab === 'image' && <div className="space-y-3">
                          <ImageModelSelector selectedModel={selectedImageModel} onModelSelect={async model => {
                      // Set loading state and update model immediately for UI responsiveness
                      setIsLoadingImageModelInputs(true);
                      // Set model without inputs first so user sees selection change
                      setSelectedImageModel(model);
                      saveScenarioPrefs({
                        selectedImageModelId: model.id
                      });
                      try {
                        // Fetch model inputs lazily when selected
                        if (!model.inputs) {
                          const inputs = await scenarioClient.getModelInputs(model.id);
                          if (inputs) {
                            // Update with inputs - this will trigger capabilities recalculation
                            const updatedModel = {
                              ...model,
                              inputs,
                              supportedParams: inputs.map(i => i.name)
                            };
                            setSelectedImageModel(prev => prev?.id === model.id ? updatedModel : prev);
                            // Initialize dynamic params for this model
                            initializeImageModelParams(updatedModel);
                            // Log model info for debugging
                            console.log('📸 Image model selected:', {
                              id: updatedModel.id,
                              name: updatedModel.name,
                              type: updatedModel.type,
                              inputs: updatedModel.inputs,
                              supportedParams: updatedModel.supportedParams
                            });
                          } else {
                            // Model doesn't have inputs in API - use empty array to indicate loaded but no inputs
                            const updatedModel = {
                              ...model,
                              inputs: [],
                              supportedParams: []
                            };
                            setSelectedImageModel(prev => prev?.id === model.id ? updatedModel : prev);
                            initializeImageModelParams(updatedModel);
                            console.log('📸 Image model selected (no inputs from API):', {
                              id: updatedModel.id,
                              name: updatedModel.name,
                              type: updatedModel.type,
                              inputs: updatedModel.inputs,
                              supportedParams: updatedModel.supportedParams
                            });
                          }
                        } else {
                          // Model already has inputs, just initialize params
                          initializeImageModelParams(model);
                          console.log('📸 Image model selected:', {
                            id: model.id,
                            name: model.name,
                            type: model.type,
                            inputs: model.inputs,
                            supportedParams: model.inputs?.map(i => i.name)
                          });
                        }
                      } catch (error) {
                        console.error('Failed to fetch model inputs:', error);
                      } finally {
                        setIsLoadingImageModelInputs(false);
                      }
                    }} disabled={isGenerating || isLoadingImageModelInputs} />
                          {/* Loading indicator for model inputs */}
                          {isLoadingImageModelInputs && <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Loading model parameters...
                            </div>}
                          {/* Model Type Indicator */}
                          {!isLoadingImageModelInputs && currentModelCapabilities && <div className="text-xs text-muted-foreground">
                              Model Type: {currentModelCapabilities.isFlux ? 'FLUX' : currentModelCapabilities.isSdxl ? 'SDXL' : currentModelCapabilities.isGptImage ? 'GPT-Image' : 'Standard'}
                              {currentModelCapabilities.isFluxPro1_1 && <span className="text-yellow-600 ml-2">⚡ Optimized settings</span>}
                            </div>}
                        </div>}
                      {activeTab === '3d' && <Model3DSelector selectedModel={selectedModel3D} onModelSelect={model => {
                    setSelectedModel3D(model);
                    initializeModel3DParams(model);
                    saveScenarioPrefs({
                      selectedModel3DId: model.id
                    });
                    console.log('🎲 3D model selected:', {
                      id: model.id,
                      name: model.name,
                      inputs: model.inputs,
                      supportedParams: model.inputs?.map(i => i.name)
                    });
                  }} disabled={isGenerating} />}
                    </CollapsibleContent>
                  </Collapsible>}

                {/* Prompt Section - Only show for non-3D tabs */}
                {activeTab !== '3d' && <Collapsible open={promptExpanded} onOpenChange={setPromptExpanded} className="p-1 rounded-lg bg-background/50 shadow-sm">
                    <CollapsibleTrigger asChild>
                      <Button variant="transparent" className="w-full justify-between font-medium">
                        <span>Prompt</span>
                        {promptExpanded ? <ChevronDown className="h-4 w-4 transition-transform duration-200" /> : <ChevronRight className="h-4 w-4 transition-transform duration-200" />}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-1 border-t">
                      <div className="space-y-2">
                        <AutosizeTextarea placeholder={activeTab === 'skybox' ? 'Describe your scene, and use the magic prompt to enhance it. You can use a reference image to inform the generation.' : activeTab === 'texture' ? 'Describe your texture, and use the magic prompt to enhance it. You can use a reference image to inform the generation.' : 'Describe your image, and use the magic prompt to enhance it. You can use a reference image to inform the generation.'} value={getCurrentPrompt()} onChange={e => setCurrentPrompt(e.target.value)} minHeight={100} maxHeight={300} />
                        <Button size="xs" onClick={handleEnhancePrompt} disabled={isEnhancingPrompt || !isMagicPromptAvailable({
                      activeTab,
                      currentPrompt: getCurrentPrompt(),
                      referenceImage: getCurrentReferenceImage(),
                      selectedModel: (() => {
                        switch (activeTab as GenerationType) {
                          case 'texture':
                            return selectedModel;
                          case 'image':
                            return selectedImageModel;
                          case '3d':
                            return selectedModel3D;
                          case 'skybox':
                            return null;
                          default:
                            return null;
                        }
                      })()
                    }).available} className="w-full" data-state={isEnhancingPrompt ? 'on' : 'off'} tooltip={isEnhancingPrompt ? 'Enhancing your prompt with AI...' : getMagicPromptDescription(activeTab) + (getCurrentPrompt().trim() ? ` (${getModeExplanation(getCurrentPrompt().length)})` : '')}>
                          {isEnhancingPrompt ? <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Enhancing...
                            </> : <>
                              <Sparkles className="mr-2 h-4 w-4" />
                              Magic Prompt
                            </>}
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>}

                {/* Generate Button Section - Between Prompt and Reference Image (except for 3D where it's after Image Views) */}
                {activeTab !== '3d' && <div className="p-2">
                    <GenerateButton activeTab={activeTab} isGenerating={isGenerating} isLoadingImageModelInputs={isLoadingImageModelInputs} textureGenerateDisableReason={textureGenerateDisableReason} imageGenerateDisableReason={imageGenerateDisableReason} is3DGenerateDisabled={is3DGenerateDisabled} model3DGenerateDisableReason={model3DGenerateDisableReason} onGenerate={handleGenerate} />
                  </div>}

                {/* Reference Image Section - only show for non-3D tabs OR 3D models with file inputs */}
                {(activeTab !== '3d' || selectedModel3D?.inputs?.some(input => input.type === 'file') || getModel3DType(selectedModel3D) === 'img23d') && <Collapsible open={referenceExpanded} onOpenChange={setReferenceExpanded} className="p-1 rounded-lg bg-background/50 shadow-sm">
                    <CollapsibleTrigger asChild>
                      <Button variant="transparent" className="w-full justify-between font-medium">
                        <span>{activeTab === '3d' ? 'Image Views' : 'Reference Image'}</span>
                        {referenceExpanded ? <ChevronDown className="h-4 w-4 transition-transform duration-200" /> : <ChevronRight className="h-4 w-4 transition-transform duration-200" />}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-2">
                      {activeTab === '3d' ? <div className="space-y-3">
                          {/* Dynamic image selectors based on model inputs */}
                          {selectedModel3D?.inputs ? selectedModel3D.inputs.filter(input => input.type === 'file').map(input => <div key={input.name} className="space-y-2">
                                  <div className="flex items-center gap-1">
                                    <Label className="text-sm font-medium">
                                      {input.label}
                                      {input.required?.always && <span className="text-destructive ml-1">*</span>}
                                    </Label>
                                    {input.description && <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="max-w-[300px]">
                                          <p className="text-xs">{input.description}</p>
                                        </TooltipContent>
                                      </Tooltip>}
                                  </div>
                                  <ReferenceImageSelector referenceImage={model3DImages[input.name] || null} onReferenceImageChange={image => setModel3DImages(prev => ({
                        ...prev,
                        [input.name]: image
                      }))} disabled={isGenerating} firebaseUid={firebaseUser?.sub} />
                                </div>) :
                    // Fallback for img23d models without inputs defined
                    <div className="space-y-2">
                              <Label className="text-sm font-medium">Front View (Required)</Label>
                              <p className="text-xs text-muted-foreground">
                                Upload the front view of your subject. The AI will generate a 3D model from this perspective.
                              </p>
                              <ReferenceImageSelector referenceImage={model3DImages.image || null} onReferenceImageChange={image => setModel3DImages(prev => ({
                        ...prev,
                        image
                      }))} disabled={isGenerating} firebaseUid={firebaseUser?.sub} />
                            </div>}
                        </div> : <ReferenceImageSelector referenceImage={getCurrentReferenceImage()} onReferenceImageChange={setCurrentReferenceImage} disabled={isGenerating} firebaseUid={firebaseUser?.sub} />}
                    </CollapsibleContent>
                  </Collapsible>}

                {/* Generate Button Section for 3D - After Image Views, before Settings */}
                {activeTab === '3d' && <div className="p-2">
                    <GenerateButton activeTab={activeTab} isGenerating={isGenerating} isLoadingImageModelInputs={isLoadingImageModelInputs} textureGenerateDisableReason={textureGenerateDisableReason} imageGenerateDisableReason={imageGenerateDisableReason} is3DGenerateDisabled={is3DGenerateDisabled} model3DGenerateDisableReason={model3DGenerateDisableReason} onGenerate={handleGenerate} />
                  </div>}

                {/* Settings Section */}
                <Collapsible open={settingsExpanded} onOpenChange={setSettingsExpanded} className="p-1 rounded-lg bg-background/50 shadow-sm">
                  <CollapsibleTrigger asChild>
                    <Button variant="transparent" className="w-full justify-between font-medium">
                      <span>Settings</span>
                      {settingsExpanded ? <ChevronDown className="h-4 w-4 transition-transform duration-200" /> : <ChevronRight className="h-4 w-4 transition-transform duration-200" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="p-2">
                    <div className="space-y-3">
                      {/* Magic Prompt Test Button - Development Only */}
                      {SHOW_MagicPromptTestButton && <div className="space-y-2">
                          <Button size="sm" onClick={() => {
                        const currentModel = (() => {
                          switch (activeTab as GenerationType) {
                            case 'texture':
                              return selectedModel;
                            case 'image':
                              return selectedImageModel;
                            case '3d':
                              return selectedModel3D;
                            case 'skybox':
                              return null;
                            default:
                              return null;
                          }
                        })();
                        const options = {
                          activeTab,
                          currentPrompt: getCurrentPrompt(),
                          referenceImage: getCurrentReferenceImage(),
                          selectedModel: currentModel
                        };
                        const availability = isMagicPromptAvailable(options);
                        console.log('🔧 === MAGIC PROMPT DIAGNOSTIC ===');
                        console.log('Current Tab:', activeTab);
                        console.log('Current Prompt:', getCurrentPrompt());
                        console.log('Prompt Length:', getCurrentPrompt().length);
                        console.log('Selected Mode:', getCurrentPrompt().length < 100 ? 'structured' : 'completion');
                        console.log('Mode Explanation:', getModeExplanation(getCurrentPrompt().length));
                        console.log('Has Reference Image:', !!getCurrentReferenceImage());
                        console.log('Has Model Context:', !!currentModel?.id);
                        console.log('Model ID:', currentModel?.id || 'none');
                        console.log('Available:', availability.available);
                        console.log('Reason:', availability.reason || 'ready to enhance');
                        console.log('Description:', getMagicPromptDescription(activeTab));
                        console.log('=== END DIAGNOSTIC ===');
                      }} className="w-full text-xs h-6">
                            🔧 Magic Prompt Diagnostic
                          </Button>
                        </div>}

                      {/* Test User Switcher - Development Only */}
                      {TEST_USER_OVERRIDE && <div className="space-y-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                          <div className="flex items-center space-x-1">
                            <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
                            <Label className="text-xs font-medium text-yellow-700 dark:text-yellow-300">
                              🧪 Test Mode Active
                            </Label>
                          </div>
                          <div className="text-[10px] text-yellow-600 dark:text-yellow-400">
                            Current Test User: <strong>{TEST_USERS[TEST_USER_OVERRIDE].displayName}</strong>
                            <br />
                            User ID: <code className="text-[10px]">{TEST_USERS[TEST_USER_OVERRIDE].uid}</code>
                            <br />
                            <em>Assets will be tagged with this user ID for testing multi-user filtering.</em>
                          </div>
                          <div className="text-[10px] text-yellow-600 dark:text-yellow-400">
                            To change test user, edit <code>TEST_USER_OVERRIDE</code> in <code>src/const.ts</code>
                          </div>
                        </div>}

                      {/* Tab-specific controls */}
                      {activeTab === 'skybox' && <div className="space-y-3">
                          <SkyboxStyleSelector selectedStyle={selectedSkyboxStyle} onStyleSelect={style => {
                        setSelectedSkyboxStyle(style);
                        saveScenarioPrefs({
                          selectedSkyboxStyle: style
                        });
                      }} disabled={isGenerating} />

                          <div className="flex items-center gap-2">
                            <Label className="min-w-[80px] text-sm">Skybox Count</Label>
                            <Slider value={[skyboxCount]} onValueChange={value => setSkyboxCount(value[0])} min={1} max={4} step={1} className="flex-1" />
                            <div className="text-xs text-muted-foreground min-w-[16px]">{skyboxCount}</div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Label className="min-w-[80px] text-sm">Sampling Steps</Label>
                            <Slider value={[skyboxSamplingSteps]} onValueChange={value => setSkyboxSamplingSteps(value[0])} min={20} max={50} step={1} className="flex-1" />
                            <div className="text-xs text-muted-foreground min-w-[16px]">{skyboxSamplingSteps}</div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Label className="min-w-[80px] text-sm">360 Scale Value</Label>
                            <Slider value={[geometryEnforcement]} onValueChange={value => setGeometryEnforcement(value[0])} min={0} max={100} step={1} className="flex-1" />
                            <div className="text-xs text-muted-foreground min-w-[16px]">{geometryEnforcement}%</div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm">Seed (optional)</Label>
                            <InputNumber placeholder="Random" value={skyboxSeed ?? 0} onChange={value => setSkyboxSeed(value === 0 ? undefined : value)} min={0} step={1} decimalPlaces={0} />
                          </div>

                          <div className="grid gap-2">
                            <div className="flex items-center gap-1">
                              <Label className="text-sm font-medium">Save Directory</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-[300px]">
                                  <p className="text-xs">Directory where skyboxes will be saved</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="flex gap-2">
                              <Popover modal={true} open={skyboxPathPickerOpen} onOpenChange={setSkyboxPathPickerOpen}>
                                <PopoverTrigger asChild>
                                  <Button type="button" variant="select" role="combobox" aria-expanded={skyboxPathPickerOpen} className={cn('justify-between text-xs h-7 flex-1', !skyboxSaveFolder && 'text-muted-foreground')} tabIndex={0}>
                                    {skyboxSaveFolder || 'Select directory...'}
                                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                                  <Command variant="select">
                                    <CommandInput placeholder="Search directory..." />
                                    <CommandList>
                                      <CommandEmpty>No directories found.</CommandEmpty>
                                      <CommandGroup>
                                        {directories.map(dir => <CommandItem variant="select" className={cn(skyboxSaveFolder === dir.path && 'bg-primary/50')} data-selected={skyboxSaveFolder === dir.path} key={dir.path} value={dir.path} onSelect={path => {
                                      skyboxSaveFolderStorage.set(path);
                                      setSkyboxPathPickerOpen(false);
                                    }}>
                                            <div className="flex items-center w-full" style={{
                                        paddingLeft: `${dir.depth * 12}px`
                                      }}>
                                              <FolderIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                                              <span className="flex items-center text-2xs font-medium">{dir.name}</span>
                                            </div>
                                          </CommandItem>)}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              <Button type="button" variant="ghost" size="icon" onClick={() => skyboxSaveFolderStorage.set('assets/generated/skybox')} className="h-7 w-7 shrink-0" tooltip="Reset to default">
                                <RotateCcwIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>}

                      {activeTab === 'texture' && <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Label className="min-w-[80px] text-sm">Texture Count</Label>
                            <Slider value={[textureCount]} onValueChange={value => setTextureCount(value[0])} min={1} max={8} step={1} className="flex-1" />
                            <div className="text-xs text-muted-foreground min-w-[16px]">{textureCount}</div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-sm">Width</Label>
                              <InputNumber value={textureWidth} onChange={value => setTextureWidth(value)} min={256} max={1024} step={64} decimalPlaces={0} />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Height</Label>
                              <InputNumber value={textureHeight} onChange={value => setTextureHeight(value)} min={256} max={1024} step={64} decimalPlaces={0} />
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Label className="min-w-[80px] text-sm">Sampling Steps</Label>
                            <Slider value={[textureSamplingSteps]} onValueChange={value => setTextureSamplingSteps(value[0])} min={20} max={50} step={1} className="flex-1" />
                            <div className="text-xs text-muted-foreground min-w-[16px]">{textureSamplingSteps}</div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Label className="min-w-[80px] text-sm">Guidance</Label>
                            <Slider value={[guidance]} onValueChange={value => setGuidance(value[0])} min={1} max={20} step={0.5} className="flex-1" />
                            <div className="text-xs text-muted-foreground min-w-[16px]">{guidance}</div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm">Seed (optional)</Label>
                            <InputNumber placeholder="Random" value={textureSeed ?? 0} onChange={value => setTextureSeed(value === 0 ? undefined : value)} min={0} step={1} decimalPlaces={0} />
                          </div>

                          <div className="flex items-center space-x-4 text-foreground">
                            <Label className="flex items-center space-x-2">
                              <Checkbox checked={tiling} onCheckedChange={checked => setTiling(checked === true)} size="sm" />
                              <span className="text-sm">Tiling</span>
                            </Label>
                            <Label className="flex items-center space-x-2">
                              <Checkbox checked={seamless} onCheckedChange={checked => setSeamless(checked === true)} size="sm" />
                              <span className="text-sm">Seamless</span>
                            </Label>
                          </div>

                          <div className="grid gap-2">
                            <div className="flex items-center gap-1">
                              <Label className="text-sm font-medium">Save Directory</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-[300px]">
                                  <p className="text-xs">Directory where textures will be saved</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="flex gap-2">
                              <Popover modal={true} open={texturePathPickerOpen} onOpenChange={setTexturePathPickerOpen}>
                                <PopoverTrigger asChild>
                                  <Button type="button" variant="select" role="combobox" aria-expanded={texturePathPickerOpen} className={cn('justify-between text-xs h-7 flex-1', !textureSaveFolder && 'text-muted-foreground')} tabIndex={0}>
                                    {textureSaveFolder || 'Select directory...'}
                                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                                  <Command variant="select">
                                    <CommandInput placeholder="Search directory..." />
                                    <CommandList>
                                      <CommandEmpty>No directories found.</CommandEmpty>
                                      <CommandGroup>
                                        {directories.map(dir => <CommandItem variant="select" className={cn(textureSaveFolder === dir.path && 'bg-primary/50')} data-selected={textureSaveFolder === dir.path} key={dir.path} value={dir.path} onSelect={path => {
                                      textureSaveFolderStorage.set(path);
                                      setTexturePathPickerOpen(false);
                                    }}>
                                            <div className="flex items-center w-full" style={{
                                        paddingLeft: `${dir.depth * 12}px`
                                      }}>
                                              <FolderIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                                              <span className="flex items-center text-2xs font-medium">{dir.name}</span>
                                            </div>
                                          </CommandItem>)}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              <Button type="button" variant="ghost" size="icon" onClick={() => textureSaveFolderStorage.set('assets/generated/textures')} className="h-7 w-7 shrink-0" tooltip="Reset to default">
                                <RotateCcwIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>}

                      {activeTab === 'image' && <div className="space-y-3">
                          {/* Loading indicator when model inputs are being fetched */}
                          {isLoadingImageModelInputs && <div className="text-xs text-muted-foreground flex items-center gap-2 p-2 bg-muted/50 rounded">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Loading model parameters...
                            </div>}

                          {/* Conditionally show Image Count based on model capabilities */}
                          {currentModelCapabilities?.showNumSamples && <div className="flex items-center gap-2">
                              <Label className="min-w-[80px] text-sm">Image Count</Label>
                              <Slider value={[imageCount]} onValueChange={value => setImageCount(value[0])} min={1} max={8} step={1} disabled={isLoadingImageModelInputs} />
                              <div className="text-xs text-muted-foreground min-w-[16px]">{imageCount}</div>
                            </div>}

                          {/* Dynamic Dimensions/Aspect Ratio Dropdown */}
                          {currentModelCapabilities?.showDimensions && <div className="space-y-2">
                              <Label className="text-sm">{usesAspectRatioOnly ? 'Aspect Ratio' : 'Dimensions'}</Label>
                              {usesAspectRatioOnly && aspectRatioInput?.allowedValues?.length ?
                        // Show aspectRatio dropdown for models that only support aspectRatio
                        <Select value={imageAspectRatio} onValueChange={setImageAspectRatio} disabled={isLoadingImageModelInputs}>
                                  <SelectTrigger className="text-xs h-7">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {aspectRatioInput.allowedValues.map(ratio => <SelectItem key={ratio} value={ratio}>
                                        {ratio}
                                      </SelectItem>)}
                                  </SelectContent>
                                </Select> :
                        // Show pixel dimensions dropdown for models that support width/height
                        <Select value={`${imageWidth}x${imageHeight}`} onValueChange={value => {
                          const [width, height] = value.split('x').map(Number);
                          setImageWidth(width);
                          setImageHeight(height);
                        }} disabled={isLoadingImageModelInputs}>
                                  <SelectTrigger className="text-xs h-7">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableDimensions.map((dim: {
                              width: number;
                              height: number;
                              label: string;
                            }) => <SelectItem key={`${dim.width}x${dim.height}`} value={`${dim.width}x${dim.height}`}>
                                        {dim.label}
                                      </SelectItem>)}
                                  </SelectContent>
                                </Select>}
                            </div>}

                          {/* Conditionally show Sampling Steps based on model capabilities */}
                          {currentModelCapabilities?.showSteps && <div className="flex items-center gap-2">
                              <Label className="min-w-[80px] text-sm">Sampling Steps</Label>
                              <Slider value={[imageSamplingSteps]} onValueChange={value => setImageSamplingSteps(value[0])} min={currentModelCapabilities?.stepsRange.min ?? 10} max={currentModelCapabilities?.stepsRange.max ?? 50} step={1} className="flex-1" disabled={isLoadingImageModelInputs} />
                              <div className="text-xs text-muted-foreground min-w-[16px]">{imageSamplingSteps}</div>
                            </div>}

                          {/* Conditionally show Guidance based on model capabilities */}
                          {currentModelCapabilities?.showGuidance && <div className="flex items-center gap-2">
                              <Label className="min-w-[80px] text-sm">Guidance</Label>
                              <Slider value={[imageGuidance]} onValueChange={value => setImageGuidance(value[0])} min={currentModelCapabilities?.guidanceRange.min ?? 1} max={currentModelCapabilities?.guidanceRange.max ?? 20} step={0.5} className="flex-1" disabled={isLoadingImageModelInputs} />
                              <div className="text-xs text-muted-foreground min-w-[16px]">{imageGuidance}</div>
                            </div>}

                          {/* Show FLUX Pro 1.1 info if applicable */}
                          {currentModelCapabilities?.isFluxPro1_1 && <div className="text-xs text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border border-yellow-200">
                              ⚡ FLUX Pro 1.1 uses optimized settings with fewer steps and simplified guidance for faster, high-quality results.
                            </div>}

                          {/* Negative Prompt (conditionally shown) */}
                          {currentModelCapabilities?.supportsNegativePrompt && <div className="space-y-2">
                              <Label className="text-sm">Negative Prompt (optional)</Label>
                              <AutosizeTextarea placeholder="What you don't want to see..." value={negativePrompt} onChange={e => setNegativePrompt(e.target.value)} minHeight={60} maxHeight={200} className="text-xs" disabled={isLoadingImageModelInputs} />
                            </div>}

                          {/* Conditionally show Seed based on model capabilities */}
                          {currentModelCapabilities?.showSeed && <div className="space-y-2">
                              <Label className="text-sm">Seed (optional)</Label>
                              <InputNumber placeholder="Random" value={imageSeed ?? 0} onChange={value => setImageSeed(value === 0 ? undefined : value)} min={0} step={1} decimalPlaces={0} className="text-xs h-7" disabled={isLoadingImageModelInputs} />
                            </div>}

                          {/* Dynamic model-specific parameters */}
                          {selectedImageModel?.inputs?.filter(input => !HANDLED_IMAGE_PARAMS.has(input.name) && input.type !== 'file' && input.type !== 'assetId' && input.type !== 'assetId[]').map(input => <div key={input.name} className="space-y-2">
                              <div className="flex items-center gap-1">
                                <Label className="text-sm">{input.label ?? input.name}</Label>
                                {input.description && <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[300px]">
                                      <p className="text-xs">{input.description}</p>
                                    </TooltipContent>
                                  </Tooltip>}
                              </div>
                              {input.type === 'boolean' ? <div className="flex items-center gap-2">
                                  <Switch checked={imageModelParams[input.name] === true} onCheckedChange={checked => setImageModelParams(prev => ({
                            ...prev,
                            [input.name]: checked
                          }))} disabled={isLoadingImageModelInputs} />
                                </div> : input.type === 'string' && input.allowedValues?.length ? <Select value={String(imageModelParams[input.name] ?? input.default ?? input.allowedValues[0])} onValueChange={value => setImageModelParams(prev => ({
                          ...prev,
                          [input.name]: value
                        }))} disabled={isLoadingImageModelInputs}>
                                  <SelectTrigger className="text-xs h-7">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {input.allowedValues.map(val => <SelectItem key={val} value={val}>
                                        {val}
                                      </SelectItem>)}
                                  </SelectContent>
                                </Select> : input.type === 'number' ? <div className="flex items-center gap-2">
                                  {input.min !== undefined && input.max !== undefined ? <>
                                      <Slider value={[Number(imageModelParams[input.name] ?? input.default ?? input.min)]} onValueChange={value => setImageModelParams(prev => ({
                              ...prev,
                              [input.name]: value[0]
                            }))} min={input.min} max={input.max} step={input.max - input.min > 10 ? 1 : 0.1} className="flex-1" disabled={isLoadingImageModelInputs} />
                                      <div className="text-xs text-muted-foreground min-w-[32px] text-right">
                                        {imageModelParams[input.name] ?? input.default ?? input.min}
                                      </div>
                                    </> : <InputNumber value={Number(imageModelParams[input.name] ?? input.default ?? 0)} onChange={value => setImageModelParams(prev => ({
                            ...prev,
                            [input.name]: value
                          }))} min={input.min} max={input.max} step={1} decimalPlaces={0} className="text-xs h-7" disabled={isLoadingImageModelInputs} />}
                                </div> : <Input value={String(imageModelParams[input.name] ?? '')} onChange={e => setImageModelParams(prev => ({
                          ...prev,
                          [input.name]: e.target.value
                        }))} placeholder={input.description ?? input.label ?? input.name} className="text-xs h-7" disabled={isLoadingImageModelInputs} />}
                            </div>)}

                          <div className="grid gap-2">
                            <div className="flex items-center gap-1">
                              <Label className="text-sm font-medium">Save Directory</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-[300px]">
                                  <p className="text-xs">Directory where images will be saved</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="flex gap-2">
                              <Popover modal={true} open={imagePathPickerOpen} onOpenChange={setImagePathPickerOpen}>
                                <PopoverTrigger asChild>
                                  <Button type="button" variant="select" role="combobox" aria-expanded={imagePathPickerOpen} className={cn('justify-between text-xs h-7 flex-1', !imageSaveFolder && 'text-muted-foreground')} tabIndex={0}>
                                    {imageSaveFolder || 'Select directory...'}
                                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                                  <Command variant="select">
                                    <CommandInput placeholder="Search directory..." />
                                    <CommandList>
                                      <CommandEmpty>No directories found.</CommandEmpty>
                                      <CommandGroup>
                                        {directories.map(dir => <CommandItem variant="select" className={cn(imageSaveFolder === dir.path && 'bg-primary/50')} data-selected={imageSaveFolder === dir.path} key={dir.path} value={dir.path} onSelect={path => {
                                      imageSaveFolderStorage.set(path);
                                      setImagePathPickerOpen(false);
                                    }}>
                                            <div className="flex items-center w-full" style={{
                                        paddingLeft: `${dir.depth * 12}px`
                                      }}>
                                              <FolderIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                                              <span className="flex items-center text-2xs font-medium">{dir.name}</span>
                                            </div>
                                          </CommandItem>)}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              <Button type="button" variant="ghost" size="icon" onClick={() => imageSaveFolderStorage.set('assets/generated/images')} className="h-7 w-7 shrink-0" tooltip="Reset to default">
                                <RotateCcwIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>}

                      {activeTab === '3d' && <div className="space-y-3">
                          {/* Dynamic form generation based on selected model's inputs */}
                          {selectedModel3D?.inputs && <div className="space-y-3">
                              {/* Only render non-file inputs here (file inputs are in Image Views section above) */}
                              {selectedModel3D.inputs.filter(input => input.type !== 'file').map(input => {
                          if (input.type === 'boolean') {
                            return <div key={input.name} className="flex items-center space-x-4">
                                        <Label className="flex items-center space-x-2">
                                          <Checkbox checked={typeof (model3DParams[input.name] ?? input.default) === 'boolean' ? (model3DParams[input.name] ?? input.default) as boolean : false} onCheckedChange={checked => setModel3DParams(prev => ({
                                  ...prev,
                                  [input.name]: checked === true
                                }))} size="sm" />
                                          <span className="text-sm">{input.label}</span>
                                          {input.description && <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                              </TooltipTrigger>
                                              <TooltipContent side="right" className="max-w-[300px]">
                                                <p className="text-xs">{input.description}</p>
                                              </TooltipContent>
                                            </Tooltip>}
                                        </Label>
                                      </div>;
                          } else if (input.type === 'number') {
                            const currentValue = typeof (model3DParams[input.name] ?? input.default) === 'number' ? (model3DParams[input.name] ?? input.default) as number : 0;
                            const min = input.min ?? 0;
                            const max = input.max ?? 100;
                            const step = input.name === 'targetFaceNum' ? 100 : input.name.toLowerCase().includes('guidance') ? 0.5 : 1;
                            return <div key={input.name} className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <div className="flex items-center gap-1 min-w-[80px]">
                                            <Label className="text-sm">{input.label}</Label>
                                            {input.description && <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                                </TooltipTrigger>
                                                <TooltipContent side="right" className="max-w-[300px]">
                                                  <p className="text-xs">{input.description}</p>
                                                </TooltipContent>
                                              </Tooltip>}
                                          </div>
                                          <Slider value={[Number(currentValue)]} onValueChange={value => setModel3DParams(prev => ({
                                  ...prev,
                                  [input.name]: value[0]
                                }))} min={min} max={max} step={step} className="flex-1" />
                                          {input.name === 'targetFaceNum' ? <InputNumber value={Number(currentValue)} onChange={value => {
                                  const clampedValue = Math.min(Math.max(value, min), max);
                                  const roundedValue = Math.round(clampedValue / 100) * 100;
                                  setModel3DParams(prev => ({
                                    ...prev,
                                    [input.name]: roundedValue
                                  }));
                                }} min={min} max={max} step={100} decimalPlaces={0} className="w-16 text-xs h-7 text-center" /> : <div className="text-xs text-muted-foreground min-w-[16px]">{currentValue}</div>}
                                        </div>
                                      </div>;
                          } else if (input.type === 'string') {
                            return <div key={input.name} className="space-y-2">
                                        <div className="flex items-center gap-1">
                                          <Label className="text-sm">
                                            {input.label}
                                            {input.required?.always && <span className="text-destructive ml-1">*</span>}
                                          </Label>
                                          {input.description && <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                              </TooltipTrigger>
                                              <TooltipContent side="right" className="max-w-[300px]">
                                                <p className="text-xs">{input.description}</p>
                                              </TooltipContent>
                                            </Tooltip>}
                                        </div>
                                        <AutosizeTextarea placeholder={input.label} value={(model3DParams[input.name] ?? input.default ?? '') as string} onChange={e => setModel3DParams(prev => ({
                                ...prev,
                                [input.name]: e.target.value
                              }))} minHeight={60} maxHeight={200} />
                                      </div>;
                          }
                          return null;
                        })}
                            </div>}

                          <div className="grid gap-2">
                            <div className="flex items-center gap-1">
                              <Label className="text-sm font-medium">Save Directory</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-muted-foreground cursor-help text-base">ⓘ</span>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-[300px]">
                                  <p className="text-xs">Directory where 3D models will be saved</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="flex gap-2">
                              <Popover modal={true} open={threeDPathPickerOpen} onOpenChange={setThreeDPathPickerOpen}>
                                <PopoverTrigger asChild>
                                  <Button type="button" variant="select" role="combobox" aria-expanded={threeDPathPickerOpen} className={cn('justify-between text-xs h-7 flex-1', !threeDSaveFolder && 'text-muted-foreground')} tabIndex={0}>
                                    {threeDSaveFolder || 'Select directory...'}
                                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                                  <Command variant="select">
                                    <CommandInput placeholder="Search directory..." />
                                    <CommandList>
                                      <CommandEmpty>No directories found.</CommandEmpty>
                                      <CommandGroup>
                                        {directories.map(dir => <CommandItem variant="select" className={cn(threeDSaveFolder === dir.path && 'bg-primary/50')} data-selected={threeDSaveFolder === dir.path} key={dir.path} value={dir.path} onSelect={path => {
                                      threeDSaveFolderStorage.set(path);
                                      setThreeDPathPickerOpen(false);
                                    }}>
                                            <div className="flex items-center w-full" style={{
                                        paddingLeft: `${dir.depth * 12}px`
                                      }}>
                                              <FolderIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                                              <span className="flex items-center text-2xs font-medium">{dir.name}</span>
                                            </div>
                                          </CommandItem>)}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              <Button type="button" variant="ghost" size="icon" onClick={() => threeDSaveFolderStorage.set('assets/generated/mesh')} className="h-7 w-7 shrink-0" tooltip="Reset to default">
                                <RotateCcwIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>

            {/* Right Panel - Results and History */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="p-6 pr-5 space-y-6 overflow-y-scroll flex-1">
                {/* Current Generation Results */}
                {thumbnails.length > 0 && <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground">Current Generation</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {thumbnails.map(thumbnail => <div key={thumbnail.id} className="space-y-2">
                          <div className="bg-muted rounded-lg overflow-hidden relative border group" style={{
                      aspectRatio: activeTab === 'skybox' ? '16 / 9' // Skyboxes are typically 16:9
                      : `${textureWidth} / ${textureHeight}` // Use texture dimensions
                    }}>
                            {thumbnail.imageUrl ? <>
                                <OptimizedScenarioImage src={thumbnail.imageUrl} alt="" aspectRatio="auto" className="w-full h-full" />

                                {/* Dropdown menu - button appears on hover, dropdown opens on click */}
                                {thumbnail.status === 'completed' && thumbnail.assetId && <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="secondary" size="sm" className="h-6 w-6 p-0 bg-black/50 hover:bg-black/70 border-none">
                                          <MoreHorizontal className="h-3 w-3 text-white" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="w-36 text-xs">
                                        <DropdownMenuItem onClick={() => handleSaveImageToProject(thumbnail.assetId!, thumbnail.id)} disabled={isSaving} className="text-xs">
                                          {isSaving ? <>
                                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                              Saving...
                                            </> : <>
                                              <Download className="mr-1 h-3 w-3" />
                                              Save to Project
                                            </>}
                                        </DropdownMenuItem>
                                        {activeTab === 'skybox' && <DropdownMenuItem onClick={() => handleUpscaleImage(thumbnail.assetId!)} disabled={isUpscaling} className="text-xs">
                                            {isUpscaling ? <>
                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                Upscaling...
                                              </> : <>
                                                <ZoomIn className="mr-1 h-3 w-3" />
                                                Upscale (2x)
                                              </>}
                                          </DropdownMenuItem>}
                                        {activeTab === 'texture' && <DropdownMenuItem onClick={() => handleConvertTexture(thumbnail.assetId!)} disabled={isConverting} className="text-xs">
                                            {isConverting ? <>
                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                Converting...
                                              </> : <>
                                                <MapIcon className="mr-1 h-3 w-3" />
                                                Convert to Maps
                                              </>}
                                          </DropdownMenuItem>}
                                        {activeTab === 'image' && <>
                                            <DropdownMenuItem onClick={() => handleUpscaleImage(thumbnail.assetId!)} disabled={isUpscaling} className="text-xs">
                                              {isUpscaling ? <>
                                                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                  Upscaling...
                                                </> : <>
                                                  <ZoomIn className="mr-1 h-3 w-3" />
                                                  Upscale (2x)
                                                </>}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleRemoveBackground(thumbnail.assetId!)} disabled={isUpscaling} className="text-xs">
                                              {isUpscaling ? <>
                                                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                  Processing...
                                                </> : <>
                                                  <Trash2 className="mr-1 h-3 w-3" />
                                                  Remove Background
                                                </>}
                                            </DropdownMenuItem>
                                          </>}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>}
                              </> : <div className="w-full h-full flex items-center justify-center">
                                {thumbnail.status === 'generating' ? <Loader2 className="h-6 w-6 animate-spin" /> : thumbnail.status === 'error' ? <div className="text-destructive text-sm">Error</div> : <div className="text-muted-foreground text-sm">Queued</div>}
                              </div>}

                            {/* Progress overlay - only show percentage when actually generating */}
                            {thumbnail.status === 'generating' && thumbnail.progress > 0 && <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white">
                                <div className="text-xs font-medium">{thumbnail.progress}%</div>
                              </div>}
                          </div>
                        </div>)}
                    </div>


                  </div>}

                {/* Historical Generations */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground">
                        Recent {activeTab === 'skybox' ? 'Skyboxes' : activeTab === 'texture' ? 'Textures' : activeTab === '3d' ? '3D Models' : 'Images'}
                      </h3>
                      {isPollingForAssets && <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          <span>Checking for new assets...</span>
                        </div>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => loadHistoricalGenerations(true)} disabled={isLoadingHistory} className="h-8 w-8 p-0" title="Refresh recent generations">
                      {isLoadingHistory ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    </Button>
                  </div>

                  {historicalGenerations.length === 0 && !isLoadingHistory ? <div className="text-center text-muted-foreground py-8 text-sm">
                      No {activeTab === 'skybox' ? 'skyboxes' : activeTab === 'texture' ? 'textures' : activeTab === '3d' ? '3D models' : 'images'} generated yet
                    </div> : <div className="space-y-3">
                      {activeTab === 'skybox' ?
                  // Skybox rendering (unchanged)
                  historicalGenerations.map(generation => {
                    const gridCols = generation.assets.length === 1 ? 'grid-cols-1' : generation.assets.length === 2 ? 'grid-cols-2' : generation.assets.length === 3 ? 'grid-cols-3' : 'grid-cols-2';
                    return <div key={generation.jobId} className="space-y-2">
                              <div className="text-xs text-muted-foreground">
                                {shouldDisplayPrompt(generation.prompt) && <div className="font-medium truncate">{generation.prompt}</div>}
                                <div>{new Date(generation.createdAt).toLocaleDateString()}</div>
                              </div>
                              <div className={`grid ${gridCols} gap-2`}>
                                {generation.assets.map(asset => <div key={asset.id} className="bg-background rounded-lg overflow-hidden border hover:border-primary transition-colors cursor-pointer relative group" style={{
                          aspectRatio: `${asset.properties.width} / ${asset.properties.height}`
                        }}>
                                    <OptimizedScenarioImage src={asset.url} alt="" aspectRatio="auto" className="w-full h-full" />

                                    {/* Dropdown menu for historical images */}
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="secondary" size="sm" className="h-6 w-6 p-0 bg-black/50 hover:bg-black/70 border-none">
                                            <MoreHorizontal className="h-3 w-3 text-white" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-36 text-xs">
                                          <DropdownMenuItem onClick={() => handleSaveImageToProject(asset.id, `historical-${asset.id}`)} disabled={isSaving} className="text-xs">
                                            {isSaving ? <>
                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                Saving...
                                              </> : <>
                                                <Download className="mr-1 h-3 w-3" />
                                                Save to Project
                                              </>}
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleUpscaleImage(asset.id)} disabled={isUpscaling} className="text-xs">
                                            {isUpscaling ? <>
                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                Upscaling...
                                              </> : <>
                                                <ZoomIn className="mr-1 h-3 w-3" />
                                                Upscale (2x)
                                              </>}
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </div>)}
                              </div>
                            </div>;
                  }) : activeTab === 'texture' ?
                  // Texture rendering with grouping
                  groupedTextureItems.map((item, index) => {
                    // Render texture item
                    if (item.type === 'original') {
                      // Regular texture generation - use consistent grid like Scenario website
                      const generation = item.generation;
                      return <div key={`${generation.jobId}-${index}`} className="space-y-2">
                                <div className="text-xs text-muted-foreground">
                                  {shouldDisplayPrompt(generation.prompt) && <div className="font-medium truncate">{generation.prompt}</div>}
                                  <div>{new Date(generation.createdAt).toLocaleDateString()}</div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {generation.assets.map((asset: ScenarioAsset) => <div key={asset.id} className="bg-background rounded-lg overflow-hidden border hover:border-primary transition-colors cursor-pointer relative group" style={{
                            aspectRatio: `${asset.properties.width} / ${asset.properties.height}`
                          }}>
                                      <OptimizedScenarioImage src={asset.url} alt="" aspectRatio="auto" className="w-full h-full" />

                                      {/* Dropdown menu for historical images */}
                                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button variant="secondary" size="sm" className="h-6 w-6 p-0 bg-black/50 hover:bg-black/70 border-none">
                                              <MoreHorizontal className="h-3 w-3 text-white" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end" className="w-36 text-xs">
                                            <DropdownMenuItem onClick={() => handleSaveImageToProject(asset.id, `historical-${asset.id}`)} disabled={isSaving} className="text-xs">
                                              {isSaving ? <>
                                                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                  Saving...
                                                </> : <>
                                                  <Download className="mr-1 h-3 w-3" />
                                                  Save to Project
                                                </>}
                                            </DropdownMenuItem>
                                            {isOriginalTexture(generation) && <DropdownMenuItem onClick={() => handleConvertTexture(asset.id)} disabled={isConverting} className="text-xs">
                                                {isConverting ? <>
                                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                    Converting...
                                                  </> : <>
                                                    <MapIcon className="mr-1 h-3 w-3" />
                                                    Convert to Maps
                                                  </>}
                                              </DropdownMenuItem>}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </div>
                                    </div>)}
                                </div>
                              </div>;
                    } else {
                      // Texture set with expandable maps
                      const generation = item.generation;
                      const maps = item.maps || [];
                      const isExpanded = expandedSets.has(generation.jobId);
                      const toggleExpanded = () => {
                        const newExpanded = new Set(expandedSets);
                        if (isExpanded) {
                          newExpanded.delete(generation.jobId);
                        } else {
                          newExpanded.add(generation.jobId);
                        }
                        setExpandedSets(newExpanded);
                      };
                      return <div key={`${generation.jobId}-set-${index}`} className="space-y-3">
                                <div className="text-sm text-muted-foreground">
                                  {shouldDisplayPrompt(generation.prompt) && <div className="font-medium truncate">{generation.prompt}</div>}
                                  <div>{new Date(generation.createdAt).toLocaleDateString()}</div>
                                </div>

                                {/* Original texture with expand/collapse button */}
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <Button variant="transparent" size="sm" onClick={toggleExpanded} className="flex items-center space-x-2">
                                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      <span>
                                        Texture Set ({maps.length} maps)
                                      </span>
                                    </Button>
                                  </div>

                                  {/* Use flipbook cube as main preview for texture sets */}
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {item.flipbookAsset ?
                            // Show flipbook cube as main preview for texture sets
                            <div key={`flipbook-main-${item.flipbookAsset.id}`} className="bg-background rounded-lg overflow-hidden border hover:border-primary transition-colors cursor-pointer relative group aspect-square">
                                        <FlipbookCube imageUrl={item.flipbookAsset.url} alt="Texture Set Preview" className="w-full h-full rounded-lg" frames={7} />

                                        {/* Dropdown menu for flipbook cube */}
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button variant="secondary" size="sm" className="h-8 w-8 p-0 bg-black/50 hover:bg-black/70 border-none">
                                                <MoreHorizontal className="h-4 w-4 text-white" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-48">
                                              <DropdownMenuItem onClick={() => handleSaveImageToProject(item.flipbookAsset!.id, `historical-${item.flipbookAsset!.id}`)} disabled={isSaving}>
                                                {isSaving ? <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Saving...
                                                  </> : <>
                                                    <Download className="mr-2 h-4 w-4" />
                                                    Save Preview
                                                  </>}
                                              </DropdownMenuItem>
                                              <DropdownMenuItem onClick={() => handleSaveTextureSet(maps)} disabled={isSaving || !maps || maps.length === 0}>
                                                {isSaving ? <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Saving...
                                                  </> : <>
                                                    <MapIcon className="mr-2 h-4 w-4" />
                                                    Save All Maps ({maps.length})
                                                  </>}
                                              </DropdownMenuItem>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        </div>

                                        {/* Texture set Label */}
                                        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                          Texture Set
                                        </div>
                                      </div> :
                            // Fallback to original texture if no flipbook available (shouldn't happen for texture sets)
                            generation.assets.map((asset: ScenarioAsset) => <div key={asset.id} className="bg-background rounded-lg overflow-hidden border hover:border-primary transition-colors cursor-pointer relative group" style={{
                              aspectRatio: `${asset.properties.width} / ${asset.properties.height}`
                            }}>
                                          <OptimizedScenarioImage src={asset.url} alt="Original texture" aspectRatio="auto" className="w-full h-full" />

                                          {/* Dropdown menu for original texture */}
                                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <Button variant="secondary" size="sm" className="h-8 w-8 p-0 bg-black/50 hover:bg-black/70 border-none">
                                                  <MoreHorizontal className="h-4 w-4 text-white" />
                                                </Button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="end" className="w-48">
                                                <DropdownMenuItem onClick={() => handleSaveImageToProject(asset.id, `historical-${asset.id}`)} disabled={isSaving}>
                                                  {isSaving ? <>
                                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                      Saving...
                                                    </> : <>
                                                      <Download className="mr-2 h-4 w-4" />
                                                      Save to Project
                                                    </>}
                                                </DropdownMenuItem>
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          </div>

                                          {/* Original texture Label */}
                                          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                            Original
                                          </div>
                                        </div>)}
                                  </div>

                                  {/* Texture maps (collapsible) */}
                                  {isExpanded && <div className="pl-4 border-l-2 border-muted">
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {/* Regular texture maps */}
                                        {maps.flatMap((mapGeneration: ScenarioAssetGeneration, mapIndex) => mapGeneration.assets.map((mapAsset: ScenarioAsset, assetIndex) => <div key={`${mapGeneration.jobId}-${mapAsset.id}-${mapIndex}-${assetIndex}`} className="bg-background rounded-lg overflow-hidden border hover:border-primary transition-colors cursor-pointer relative group aspect-square">
                                              <OptimizedScenarioImage src={mapAsset.url} alt={`${getMapType(mapAsset)} map`} aspectRatio="auto" className="w-full h-full" />

                                              {/* Dropdown menu for maps */}
                                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <DropdownMenu>
                                                  <DropdownMenuTrigger asChild>
                                                    <Button variant="secondary" size="sm" className="h-8 w-8 p-0 bg-black/50 hover:bg-black/70 border-none">
                                                      <MoreHorizontal className="h-4 w-4 text-white" />
                                                    </Button>
                                                  </DropdownMenuTrigger>
                                                  <DropdownMenuContent align="end" className="w-48">
                                                    <DropdownMenuItem onClick={() => handleSaveImageToProject(mapAsset.id, `historical-${mapAsset.id}`)} disabled={isSaving}>
                                                      {isSaving ? <>
                                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                          Saving...
                                                        </> : <>
                                                          <Download className="mr-2 h-4 w-4" />
                                                          Save to Project
                                                        </>}
                                                    </DropdownMenuItem>
                                                  </DropdownMenuContent>
                                                </DropdownMenu>
                                              </div>

                                              {/* Map type Label */}
                                              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                {getMapType(mapAsset)}
                                              </div>
                                            </div>))}


                                      </div>
                                    </div>}
                                </div>
                              </div>;
                    }
                  }) : activeTab === '3d' ?
                  // 3D model rendering (vertical layout like other tabs)
                  historicalGenerations.map((generation, generationIndex) => <div key={`3d-${generation.jobId}-${generationIndex}`} className="space-y-2">
                            <div className="text-xs text-muted-foreground">
                              {shouldDisplayPrompt(generation.prompt || 'Image to 3D Generation') && <div className="font-medium truncate">{generation.prompt || 'Image to 3D Generation'}</div>}
                              <div>{new Date(generation.createdAt).toLocaleDateString()}</div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                              {generation.assets.map((asset, assetIndex) => <ThreeDGenerationItem key={`${asset.id}-${assetIndex}`} generation={generation} generationIndex={generationIndex} onSaveToProject={handleSaveImageToProject} isSaving={isSaving} />)}
                            </div>
                          </div>) :
                  // Image rendering (similar to skybox)
                  historicalGenerations.map((generation, generationIndex) => {
                    const gridCols = generation.assets.length === 1 ? 'grid-cols-2' : generation.assets.length === 2 ? 'grid-cols-2' : generation.assets.length === 3 ? 'grid-cols-3' : generation.assets.length === 4 ? 'grid-cols-4' : 'grid-cols-2';

                    // Debug logging for image generations
                    if (generation.prompt?.toLowerCase().includes('generated')) {
                      console.log('🔍 Image generation prompt:', generation.prompt, 'shouldDisplay:', shouldDisplayPrompt(generation.prompt));
                    }
                    return <div key={`image-${generation.jobId}-${generationIndex}`} className="space-y-2">
                              <div className="text-xs text-muted-foreground">
                                {shouldDisplayPrompt(generation.prompt) && <div className="font-medium truncate">{generation.prompt}</div>}
                                <div>{new Date(generation.createdAt).toLocaleDateString()}</div>
                              </div>
                              <div className={`grid ${gridCols} gap-2`}>
                                {generation.assets.map((asset, assetIndex) => <div key={`${asset.id}-${assetIndex}`} className="bg-background rounded-lg overflow-hidden border hover:border-primary transition-colors cursor-pointer relative group" style={{
                          aspectRatio: `${asset.properties.width} / ${asset.properties.height}`
                        }}>
                                    <OptimizedScenarioImage src={asset.url} alt="" aspectRatio="auto" className="w-full h-full" />

                                    {/* Dropdown menu for historical images */}
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="secondary" size="sm" className="h-6 w-6 p-0 bg-black/50 hover:bg-black/70 border-none">
                                            <MoreHorizontal className="h-3 w-3 text-white" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-36 text-xs">
                                          <DropdownMenuItem onClick={() => handleSaveImageToProject(asset.id, `historical-${asset.id}`)} disabled={isSaving} className="text-xs">
                                            {isSaving ? <>
                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                Saving...
                                              </> : <>
                                                <Download className="mr-1 h-3 w-3" />
                                                Save to Project
                                              </>}
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleUpscaleImage(asset.id)} disabled={isUpscaling} className="text-xs">
                                            {isUpscaling ? <>
                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                Upscaling...
                                              </> : <>
                                                <ZoomIn className="mr-1 h-3 w-3" />
                                                Upscale (2x)
                                              </>}
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleRemoveBackground(asset.id)} disabled={isUpscaling} className="text-xs">
                                            {isUpscaling ? <>
                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                Processing...
                                              </> : <>
                                                <Trash2 className="mr-1 h-3 w-3" />
                                                Remove Background
                                              </>}
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </div>)}
                              </div>
                            </div>;
                  })}
                    </div>}
                </div>

                {/* Error Display */}
                {error && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="text-sm text-destructive">{error}</div>
                  </div>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>;
});
ScenarioGenerationDialog.displayName = 'ScenarioGenerationDialog';
function _temp(e) {
  e.stopPropagation();
}
function _temp2(asset) {
  return asset.metadata?.format === "glb" || asset.url?.toLowerCase().includes(".glb") || !asset.url?.includes(".") && asset.url?.length > 0;
}
function _temp3(asset_0) {
  return asset_0.url?.toLowerCase().includes(".mp4") || asset_0.metadata?.format === "mp4";
}
function _temp4() {}
function _temp5(e) {
  const video = e.currentTarget;
  if (video) {
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(_temp4);
    }
  }
}
function _temp6(e_0) {
  const video_0 = e_0.currentTarget;
  if (video_0) {
    video_0.pause();
    video_0.currentTime = 0;
  }
}
function _temp7(a) {
  return a.metadata?.format || "unknown";
}